/**
 * OrganizationReports.js
 *
 * This component handles the organization reports functionality in the application.
 * It provides a comprehensive reporting interface with various report types and filtering options.
 *
 * Key features:
 * - Multiple report types (PRIME, Indiana, Unity, etc.)
 * - Date range selection
 * - Organization/Location/Program filtering
 * - Custom column selection
 * - Export functionality (CSV/Excel)
 * - Data visualization and filtering
 *
 * Large server-side exports use async jobs (gzip CSV download + polling).
 * @see ../../../docs/report-async-export.md (client UI contract)
 * Server design: soar.dev.server/docs/report-async-export.md
 */

import React, { useContext, useState, useRef, useEffect } from "react"
import SidebarDashboardSupervisor from "../../components/sidebars/SidebarDashboardSupervisor"
import PageSidebarContent from "../../components/sidebars/PageSidebarContent"
import { LogError } from "../../helpers/logger"

import _ from "lodash"
import { useQuery, useLazyQuery } from "@apollo/client"

// Prime React Components - UI library components for the reporting interface
import { Tooltip } from "primereact/tooltip"
import { DataTable } from "primereact/datatable"
import { Button } from "primereact/button"
import { Column } from "primereact/column"
import { Panel } from "primereact/panel"
import { MultiSelect } from "primereact/multiselect"
import { Toast } from "primereact/toast"
import { Message } from "primereact/message"
import { Dropdown } from "primereact/dropdown"

// Date handling components and utilities
import {
  DateRangePicker,
  defaultStaticRanges,
  defaultInputRanges,
} from "react-date-range"
import {
  subDays,
  endOfDay,
  startOfYear,
  endOfYear,
  addYears,
  isSameDay,
  format,
  isWeekend,
} from "date-fns"
import "react-date-range/dist/styles.css"
import "react-date-range/dist/theme/default.css"

// Context for user authentication and session management
import { UserContext } from "../../context/userContext"

// GraphQL Queries - Import all report-related queries
import {
  ENTERPRISE,
  ORGANIZATION,
  LOCATION,
  PROGRAM,
  STATUS,
  STAFF,
  TEMPLATE,
  BLOCK,
  COLUMN,
  CUSTOM_COLUMN,
  ISR,
  ISR_GROUP_SESSION,
  ORGANIZATION_REPORTS,
  GPRA_COLUMN,
  GPRA_REPORTS,
  MICROSERVICES_REPORT_OPTIONS,
  TELERECOVERY_SESSIONS_REPORT,
  REFERRALS_REPORT,
  INDIANA_OS_REPORT,
  INDIANA_ACTIVITY_SESSION_REPORT,
  INDIANA_DEMOGRAPHICS_REPORT,
  INDIANA_DIRECT_SERVICE_REPORT,
  INDIANA_ENGAGEMENT_REPORT,
  INDIANA_GPRA_REPORT,
  INDIANA_LIST_REFERRALS_REPORT,
  INDIANA_LYFT_DROPOFF_REPORT,
  INDIANA_LYFT_REPORT,
  INDIANA_REFERRALS_REPORT,
  INDIANA_SUPERVISION_REPORT,
  INDIANA_CONTACT_LOG_REPORT,
  REPORT_ASYNC_EXPORT_STATUS,
  PRIME_CHRACTERISTICS_REPORT,
  PRIME_DEMOGRAPHICS_REPORT,
  PRIME_INTAKES_REPORT,
  PRIME_CHECK_INS_REPORT,
  PRIME_GPRA_REPORT,
  KIOSK_ACTIVITY_REPORT,
  KIOSK_CONSENT_REPORT,
  BARC10_MONTHLY_REPORT,
  BARC10_INDIVIDUAL_REPORT,
  PARTICIPANT_COLUMN,
  PARTICIPANT_CONTACT_REPORT,
  PARTICIPANT_DEMOGRAPHICS_REPORT,
  PARTICIPANT_ENGAGED_REPORT,
  PARTICIPANT_CHARACTERISTICS_REPORT,
  NH_MEDICAID_BILLING_REPORT,
} from "./queries"

import styles from "../../styles/Reporting.module.css"

// Constants for field names to avoid magic strings
const NUMERIC_FIELDS = [
  "activity_distributed",
  "activity_attendance_hr",
  "activity_attendance",
  "session_length",
]

const DOCUMENT_PARTICIPANT_FILE_KEY = "document_participant_file"
const MAX_REPORT_DATE_RANGE_YEARS = 10
const PARTICIPANT_REPORT_TYPE = "Participant Reporting"
const ORGANIZATION_REPORTING_TYPE = "Organization Reporting"
const CUSTOM_REPORTING_TYPE = "Custom Reporting"
const MICROSERVICES_REPORTING_TYPE = "Microservices Reporting"
const GPRA_REPORT_SUBTYPE = "GPRA Report"
const KIOSK_CHECK_IN_ACTIVITY_REPORT_SUBTYPE = "Kiosk Check-in Activity Report"
const REFERRALS_REPORT_SUBTYPE = "Referrals Report"
const ASSESSMENTS_BARC_10_MONTHLY_REPORT_SUBTYPE =
  "Assessments BARC-10 Monthly Report"
const ASSESSMENTS_BARC_10_REPORT_SUBTYPE = "Assessments BARC-10 Report"
const TELERECOVERY_SESSIONS_REPORT_SUBTYPE = "Telerecovery Sessions Report"

const LOCATION_FILTER_HELPTEXT_DEFAULT =
  "Filter by participant assigned location(s)"
const PROGRAM_FILTER_HELPTEXT_DEFAULT =
  "Filter by participant assigned program(s)"
const PARTICIPANT_STATUS_FILTER_HELPTEXT_DEFAULT =
  "Filter by current participant status"
const ASSIGNED_STAFF_FILTER_HELPTEXT_DEFAULT =
  "Filter by current assigned staff"
const FACILITATING_STAFF_FILTER_HELPTEXT_DEFAULT =
  "Filter by current participant assigned values"

/**
 * Utility function to extract filename from a file path
 * @param {string} pathString - The file path string
 * @returns {string|null} The filename or null if invalid input
 */
function getFilename(pathString) {
  if (typeof pathString !== "string") return null
  const parts = pathString.split("/")
  return parts[parts.length - 1] || null
}

const ASYNC_EXPORT_SUPPORT_EMAIL = "support@myrecoverylink.com"

function getAsyncExportSeverity(status) {
  if (status === "READY") return "success"
  if (
    status === "FAILED" ||
    status === "EXPIRED" ||
    status === "NOT_FOUND" ||
    status === "TIMEOUT"
  ) {
    return "error"
  }
  if (status === "PROCESSING" || status === "PENDING") {
    return "warn"
  }
  return "info"
}

function isAsyncExportInProgress(status) {
  return status === "PENDING" || status === "PROCESSING"
}

function isAsyncExportErrorStatus(status) {
  return (
    status === "FAILED" ||
    status === "EXPIRED" ||
    status === "NOT_FOUND" ||
    status === "TIMEOUT"
  )
}

function formatElapsedSeconds(totalSeconds) {
  const seconds = Math.max(0, totalSeconds || 0)
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, "0")}`
}

const ASYNC_ROW_THRESHOLD = 15000

function formatExportingRowsMessage(rowCount) {
  if (rowCount == null) {
    return null
  }
  const num = Number(rowCount)
  if (!Number.isFinite(num) || num <= 0) {
    return null
  }
  if (num > ASYNC_ROW_THRESHOLD) {
    return `More than ${ASYNC_ROW_THRESHOLD.toLocaleString()} rows exporting to CSV`
  }
  return `${Math.round(num).toLocaleString()} rows exporting to CSV`
}

function formatFileSize(bytes) {
  if (bytes == null) {
    return null
  }
  const num = Number(bytes)
  if (!Number.isFinite(num) || num <= 0) {
    return null
  }
  const mbThreshold = 1024 * 1024
  if (num < mbThreshold) {
    return `${Math.round(num / 1024).toLocaleString()} KB`
  }
  const mb = num / mbThreshold
  const formatted =
    mb >= 10
      ? Math.round(mb).toLocaleString()
      : mb.toLocaleString(undefined, { maximumFractionDigits: 1 })
  return `${formatted} MB`
}

function formatExportReadyStatsLine(rowCount, fileSizeBytes) {
  const rows =
    rowCount != null && Number.isFinite(Number(rowCount))
      ? `${Number(rowCount).toLocaleString()} rows exported`
      : null
  const fileSize = formatFileSize(fileSizeBytes)

  if (rows && fileSize) {
    return `${rows} (${fileSize} download)`
  }
  if (rows) {
    return rows
  }
  if (fileSize) {
    return `${fileSize} download`
  }
  return null
}

function getAsyncExportDownloadFilename(downloadFilename, jobId) {
  if (typeof downloadFilename === "string" && downloadFilename) {
    return downloadFilename
  }

  if (jobId) {
    return `${jobId}.zip`
  }

  return null
}

function triggerBlobDownload(blob, filename) {
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = objectUrl
  anchor.download = filename
  anchor.rel = "noopener"
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
}

/**
 * Download the async export ZIP. Prefer fetch+blob so the browser keeps the
 * `.zip` filename; fall back to opening the signed URL.
 */
async function downloadAsyncExportFile(downloadUrl, jobId, downloadFilename) {
  const filename =
    getAsyncExportDownloadFilename(downloadFilename, jobId) || "report.zip"

  try {
    const response = await fetch(downloadUrl)
    if (!response.ok) {
      throw new Error(`Download failed (${response.status})`)
    }
    const buffer = await response.arrayBuffer()
    triggerBlobDownload(
      new Blob([buffer], { type: "application/zip" }),
      filename,
    )
  } catch {
    window.open(downloadUrl, "_blank", "noopener,noreferrer")
  }
}

/**
 * Main OrganizationReports component
 * Handles the organization reports interface and functionality
 */
export default function OrganizationReports() {
  // User context for authentication and session management
  const userCtx = useContext(UserContext)
  const user = userCtx.session.user

  // State management for filters and selections
  const [organization, setOrganization] = useState(null)
  const [location, setLocation] = useState(null)
  const [program, setProgram] = useState(null)
  const [status, setStatus] = useState(null)
  const [staff, setStaff] = useState(null)
  const [facilitating_staff, setFacilitatingStaff] = useState(null)
  const [type, setType] = useState(null)
  const [subtype, setSubtype] = useState(null)
  const [template, setTemplate] = useState(null)
  const [block, setBlock] = useState(null)
  const [column, setColumn] = useState(null)
  const [custom_report_column, setCustomReportColumn] = useState(null)
  const [microservices_report_column, setMicroServicesReportColumn] =
    useState(null)
  const [participant_report_column, setParticipantReportColumn] = useState(null)
  const dt = useRef(null)
  const message = useRef(null)
  const [loading, setLoading] = useState(false)
  const [clear, setClear] = useState(false)
  const pollTimeoutRef = useRef(null)
  const pollActiveRef = useRef(false)
  const elapsedIntervalRef = useRef(null)
  const softTimeoutToastShownRef = useRef(false)
  const [asyncExportInfo, setAsyncExportInfo] = useState(null)
  const [dtfilter, setDtfilter] = useState(null)
  const [dtreorder, setDtreorder] = useState(null)

  const reportsTableSectionRef = React.useCallback(
    (node) => {
      if (!node || !clear || asyncExportInfo) {
        return
      }

      const dataTable = node.querySelector(".p-datatable")
      if (dataTable) {
        node.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    },
    [clear, asyncExportInfo],
  )

  const [locationFilterHelpText, setLocationFilterHelpText] = useState(
    LOCATION_FILTER_HELPTEXT_DEFAULT,
  )
  const [programFilterHelpText, setProgramFilterHelpText] = useState(
    PROGRAM_FILTER_HELPTEXT_DEFAULT,
  )
  const [participantStatusFilterHelpText, setParticipantStatusFilterHelpText] =
    useState(PARTICIPANT_STATUS_FILTER_HELPTEXT_DEFAULT)
  const [assignedStaffFilterHelpText, setAssignedStaffFilterHelpText] =
    useState(ASSIGNED_STAFF_FILTER_HELPTEXT_DEFAULT)
  const [facilitatingStaffFilterHelpText, setFacilitatingStaffFilterHelpText] =
    useState(FACILITATING_STAFF_FILTER_HELPTEXT_DEFAULT)

  const [date_range, setDateRange] = useState([
    {
      startDate: subDays(new Date(), 7),
      endDate: new Date(),
      key: "selection",
    },
  ])

  const { data: enterprise_data, loading: enterprise_loading } = useQuery(
    ENTERPRISE,
    {
      variables: {
        role_id: user.staff_role,
        enterprise_id: user.enterprise_id,
      },
      fetchPolicy: "network-only",
      skip: _.isEmpty(user.enterprise_id),
    },
  )

  const { data: organization_data, loading: organization_loading } = useQuery(
    ORGANIZATION,
    {
      variables: {
        role_id: user.staff_role,
        organization_id: user?.organization_id || "",
        enterprise_id: user.enterprise_id,
      },
      fetchPolicy: "network-only",
      skip: _.isEmpty(user.enterprise_id),
    },
  )

  const organization_transform = (() => {
    if (organization) {
      return organization.map((x) => "'" + x + "'").join(",")
    } else {
      return organization
    }
  })()

  const { data: location_data, loading: location_loading } = useQuery(
    LOCATION,
    {
      variables: {
        organization_id: organization_transform,
      },
      fetchPolicy: "network-only",
      skip: _.isEmpty(organization_transform),
    },
  )

  const { data: program_data, loading: program_loading } = useQuery(PROGRAM, {
    variables: {
      organization_id: organization_transform,
    },
    fetchPolicy: "network-only",
    skip: _.isEmpty(organization_transform),
  })

  const { data: status_data, loading: status_loading } = useQuery(STATUS, {
    variables: {
      organization_id: organization_transform,
    },
    fetchPolicy: "network-only",
    skip: _.isEmpty(organization_transform),
  })

  const { data: staff_data, loading: staff_loading } = useQuery(STAFF, {
    variables: {
      organization_id: organization_transform,
    },
    fetchPolicy: "network-only",
    skip: _.isEmpty(organization_transform),
  })

  const { data: template_data, loading: template_loading } = useQuery(
    TEMPLATE,
    {
      variables: {
        organization_id: organization_transform,
        report_type: subtype,
      },
      fetchPolicy: "network-only",
      skip:
        _.isEmpty(organization_transform) ||
        _.isEmpty(subtype) ||
        type !== ORGANIZATION_REPORTING_TYPE,
    },
  )

  const { data: msreportoptionsdata, error: msreportoptionserror } = useQuery(
    MICROSERVICES_REPORT_OPTIONS,
    {
      variables: {
        enterprise_id: user.enterprise_id,
      },
      fetchPolicy: "network-only",
      onError: (error) => {
        LogError("Fetching microservices report options: ", { ...error })
      },
    },
  )

  const organization_transform_name = (() => {
    if (organization) {
      const output = organization.map((x) => {
        const name = organization_data.ReportOrganization.filter(
          (item) => item.organization_id === x,
        ).map((col) => {
          return { name: col.organization_name, id: x }
        })
        return name
      })
      return output
    } else {
      return organization
    }
  })()

  const organization_options = (() => {
    if (enterprise_data && organization_data) {
      const organizations = enterprise_data.ReportEnterprise.map(
        (enterprise) => {
          const items = organization_data.ReportOrganization.filter(
            (item) => item.enterprise_id === enterprise.enterprise_id,
          ).map((col) => {
            return { value: col.organization_id, label: col.organization_name }
          })
          return { label: enterprise.enterprise_name, items: items }
        },
      )
      return organizations
    } else {
      return null
    }
  })()

  const location_options = (() => {
    if (organization && location_data) {
      const locations = organization_transform_name.map(([organization]) => {
        const items = location_data.ReportLocation.filter(
          (item) => item.organization_id === organization.id,
        ).map((col) => {
          return { value: col.location_id, label: col.location_name }
        })
        return { label: organization.name, items: items }
      })
      return locations
    } else {
      return null
    }
  })()

  const program_options = (() => {
    if (organization && program_data) {
      const programs = organization_transform_name.map(([organization]) => {
        const items = program_data.ReportProgram.filter(
          (item) => item.organization_id === organization.id,
        ).map((col) => {
          return { value: col.program_identity_id, label: col.program_id_name }
        })
        return { label: organization.name, items: items }
      })
      return programs
    } else {
      return null
    }
  })()

  const status_options = (() => {
    if (status_data) {
      return _.sortBy(
        _.uniqBy(status_data.ReportStatus, "participant_membership_status"),
        "participant_membership_status",
      ).map((col) => ({
        value: col.participant_membership_status,
        label: col.participant_membership_status,
      }))
    }

    return null
  })()

  const staff_options = (() => {
    if (organization && staff_data) {
      const staff = organization_transform_name.map(([organization]) => {
        const items = staff_data.ReportStaff.filter(
          (item) => item.organization_id === organization.id,
        ).map((col) => {
          return { value: col.staff, label: col.staff, id: col.staff_id }
        })
        return { label: organization.name, items: items }
      })
      return staff
    } else {
      return null
    }
  })()

  const report_type = [
    { value: ORGANIZATION_REPORTING_TYPE, label: ORGANIZATION_REPORTING_TYPE },
    { value: "Microservices Reporting", label: "Microservices Reporting" },
    { value: "Custom Reporting", label: "Custom Reporting" },
    { value: PARTICIPANT_REPORT_TYPE, label: PARTICIPANT_REPORT_TYPE },
  ]

  const report_sub_type = (() => {
    if (type === ORGANIZATION_REPORTING_TYPE) {
      return [
        {
          label: ORGANIZATION_REPORTING_TYPE,
          items: [
            { value: "Activity", label: "Activity" },
            { value: "Data Form", label: "Data Form" },
            { value: "Session", label: "Session" },
          ],
        },
      ]
    } else if (type === "Custom Reporting") {
      // Code to include Indiana custom reports if one or more organizations from Indiana is selected by staff
      const { ReportOrganization: organizationList } = organization_data

      const indianaOrganizations = _.reduce(
        organizationList,
        (acc, org) => {
          const indianaEntId = "68412bfd-57f4-40ad-af62-88fc003360ed"
          if (org.enterprise_id === indianaEntId) {
            return [...acc, org.organization_id]
          }
          return acc
        },
        [],
      )

      function hasIndianaOrganizationSelected() {
        if (!organization || organization.length === 0) {
          return false
        }
        for (const orgId of organization) {
          if (indianaOrganizations.includes(orgId)) {
            return true
          }
        }
        return false
      }

      if (user.staff_role === 1) {
        let defaultList = [
          {
            label: "Custom Reporting",
            items: [
              {
                value: "ISR Individual Sessions",
                label: "ISR Individual Sessions",
              },
              { value: "ISR Group Sessions", label: "ISR Group Sessions" },
              {
                value: "Activity Session",
                label: "Indiana SSA Activity Session Report",
              },
              {
                value: "Demographics",
                label: "Indiana SSA Demographics Report",
              },
              {
                value: "Direct Service Hours",
                label: "Indiana SSA Direct Service Hours Report",
              },
              { value: "Engagements", label: "Indiana SSA Engagements Report" },
              { value: "GPRA", label: "Indiana SSA GPRA Report" },
              {
                value: "Individual List of Incoming Referrals",
                label:
                  "Indiana SSA Individual List of Incoming Referrals Report",
              },
              {
                value: "Lyft Drop-off Location",
                label: "Indiana SSA Lyft Drop-off Location Report",
              },
              { value: "Lyft", label: "Indiana SSA Lyft Report" },
              { value: "Supervision", label: "Indiana SSA Supervision Report" },
              {
                value: "DOC Incoming Referrals",
                label: "Indiana SSA DOC Incoming Referrals Report",
              },
              {
                value: "New Hampshire Medicaid Billing Report",
                label: "New Hampshire Medicaid Billing Report",
              },
              {
                value: "PRIME Participant Characteristics Report",
                label: "PRIME Participant Characteristics Report",
              },
              {
                value: "PRIME Participant Demographics Report",
                label: "PRIME Participant Demographics Report",
              },

              {
                value: "PRIME Intakes Report",
                label: "PRIME Intakes Report",
              },
              {
                value: "PRIME Check-ins Report",
                label: "PRIME Check-ins Report",
              },
              {
                value: "PRIME GPRA Report",
                label: "PRIME GPRA Report",
              },
            ],
          },
        ]

        // TODO: This is a hack to add the Opioid Stimulant Specific and Contact Log custom reports
        // report to the list of reports
        // Please refactor....
        if (hasIndianaOrganizationSelected()) {
          defaultList[0].items.push({
            value: "Opioid Stimulant Specific",
            label: "Opioid Stimulant Specific",
          })
          defaultList[0].items.push({
            value: "Contact Log",
            label: "Contact Log",
          })
        }
        const sortedList = _.sortBy(defaultList[0].items, ["label"])
        defaultList[0].items = sortedList
        return defaultList
      } else if (
        user.enterprise_id === "68412bfd-57f4-40ad-af62-88fc003360ed"
      ) {
        // Indiana SSA (Enterprise) custom reports options
        // Opioid Stimulant Specific and Contact Log reports are only available to Indiana SSA staff
        return [
          {
            label: "Custom Reporting",
            items: [
              {
                value: "ISR Individual Sessions",
                label: "ISR Individual Sessions",
              },
              { value: "ISR Group Sessions", label: "ISR Group Sessions" },
              {
                value: "Activity Session",
                label: "Indiana SSA Activity Session Report",
              },
              { value: "Contact Log", label: "Contact Log" },
              {
                value: "Demographics",
                label: "Indiana SSA Demographics Report",
              },
              {
                value: "Direct Service Hours",
                label: "Indiana SSA Direct Service Hours Report",
              },
              { value: "Engagements", label: "Indiana SSA Engagements Report" },
              { value: "GPRA", label: "Indiana SSA GPRA Report" },
              {
                value: "Individual List of Incoming Referrals",
                label:
                  "Indiana SSA Individual List of Incoming Referrals Report",
              },
              {
                value: "Lyft Drop-off Location",
                label: "Indiana SSA Lyft Drop-off Location Report",
              },
              { value: "Lyft", label: "Indiana SSA Lyft Report" },
              { value: "Supervision", label: "Indiana SSA Supervision Report" },
              {
                value: "DOC Incoming Referrals",
                label: "Indiana SSA DOC Incoming Referrals Report",
              },
            ],
          },
        ]
      } else if (
        user.enterprise_id === "b48067e0-73a9-459f-ad0a-8b5856403b9d" &&
        (user.staff_role === 2 || user.staff_role === 3) // User Role update ERR-1016. Adding staff_role = 3
      ) {
        // PRIME Oregon enterprise custom reports options
        return [
          {
            label: "Custom Reporting",
            items: [
              {
                value: "ISR Individual Sessions",
                label: "ISR Individual Sessions",
              },
              { value: "ISR Group Sessions", label: "ISR Group Sessions" },
              {
                value: "PRIME Participant Characteristics Report",
                label: "PRIME Participant Characteristics Report",
              },
              {
                value: "PRIME Participant Demographics Report",
                label: "PRIME Participant Demographics Report",
              },

              {
                value: "PRIME Intakes Report",
                label: "PRIME Intakes Report",
              },
              {
                value: "PRIME Check-ins Report",
                label: "PRIME Check-ins Report",
              },
              {
                value: "PRIME GPRA Report",
                label: "PRIME GPRA Report",
              },
            ],
          },
        ]
      } else if (
        user.enterprise_id === "be6981d0-3202-44f1-aec9-d62d712642a7"
      ) {
        // New Hampshire (Enterprise) custom reports options
        return [
          {
            label: "Custom Reporting",
            items: [
              {
                value: "ISR Individual Sessions",
                label: "ISR Individual Sessions",
              },
              { value: "ISR Group Sessions", label: "ISR Group Sessions" },
              {
                value: "New Hampshire Medicaid Billing Report",
                label: "New Hampshire Medicaid Billing Report",
              },
            ],
          },
        ]
      } else {
        return [
          {
            label: "Custom Reporting",
            items: [
              {
                value: "ISR Individual Sessions",
                label: "ISR Individual Sessions",
              },
              { value: "ISR Group Sessions", label: "ISR Group Sessions" },
            ],
          },
        ]
      }
    } else if (type === "Microservices Reporting") {
      const msReportOptionsItems =
        msreportoptionsdata?.ReportMicroservicesReportOptions.map((option) => {
          return { value: option.option_name, label: option.option_name }
        })

      if (user.staff_role === 1) {
        return [
          {
            label: "Microservices Reporting",
            items: [
              { value: GPRA_REPORT_SUBTYPE, label: GPRA_REPORT_SUBTYPE },
              {
                value: KIOSK_CHECK_IN_ACTIVITY_REPORT_SUBTYPE,
                label: KIOSK_CHECK_IN_ACTIVITY_REPORT_SUBTYPE,
              },
              {
                value: REFERRALS_REPORT_SUBTYPE,
                label: REFERRALS_REPORT_SUBTYPE,
              },
              {
                value: ASSESSMENTS_BARC_10_MONTHLY_REPORT_SUBTYPE,
                label: ASSESSMENTS_BARC_10_MONTHLY_REPORT_SUBTYPE,
              },
              {
                value: ASSESSMENTS_BARC_10_REPORT_SUBTYPE,
                label: ASSESSMENTS_BARC_10_REPORT_SUBTYPE,
              },
              {
                value: TELERECOVERY_SESSIONS_REPORT_SUBTYPE,
                label: TELERECOVERY_SESSIONS_REPORT_SUBTYPE,
              },
            ],
          },
        ]
      } else {
        return [
          {
            label: "Microservices Reporting",
            items: msReportOptionsItems,
          },
        ]
      }
    } else if (type === PARTICIPANT_REPORT_TYPE) {
      return [
        {
          label: PARTICIPANT_REPORT_TYPE,
          items: [
            {
              value: "Participant Contact Information",
              label: "Participant Contact Information",
            },
            {
              value: "Participant Demographics",
              label: "Participant Demographics",
            },
            {
              value: "Participant Engaged Services",
              label: "Participant Engaged Services",
            },
            {
              value: "Participant Recovery Characteristics",
              label: "Participant Recovery Characteristics",
            },
          ],
        },
      ]
    }
  })()

  const template_options = (() => {
    if (type === ORGANIZATION_REPORTING_TYPE && template_data) {
      const templates = template_data.ReportTemplate.map((template) => {
        return {
          value: template.template_title,
          label: template.template_title,
        }
      })
      return [{ label: subtype, items: templates }]
    } else {
      return null
    }
  })()

  const template_transform = (() => {
    if (template) {
      return "'" + template + "'"
    } else {
      return template
    }
  })()

  const { data: block_data, loading: block_loading } = useQuery(BLOCK, {
    variables: {
      organization_id: organization_transform,
      template_title: template_transform,
      report_type: subtype,
    },
    fetchPolicy: "network-only",
    skip:
      _.isEmpty(organization_transform) ||
      _.isEmpty(template_transform) ||
      _.isEmpty(subtype),
  })

  const block_options = (() => {
    if (type === ORGANIZATION_REPORTING_TYPE && block_data) {
      const blocks = block_data.ReportBlock.map((col) => {
        return { value: col.block_name, label: col.block_name }
      })
      return [{ label: template, items: blocks }]
    } else {
      return null
    }
  })()

  const block_transform = (() => {
    if (block) {
      return block.map((x) => "'" + x + "'").join(",")
    } else {
      return block
    }
  })()

  const { data: column_data, loading: column_loading } = useQuery(COLUMN, {
    variables: {
      organization_id: organization_transform,
      template_title: template_transform,
      block_name: block_transform,
      report_type: subtype,
    },
    fetchPolicy: "network-only",
    skip:
      _.isEmpty(organization_transform) ||
      _.isEmpty(template_transform) ||
      _.isEmpty(block_transform) ||
      _.isEmpty(subtype),
  })

  const column_options = (() => {
    if (block && column_data) {
      const columns = block.map((blocks) => {
        const items = column_data.ReportColumn.filter(
          (item) =>
            item.template_title === template && item.block_name === blocks,
        ).map((col) => {
          return { value: col.field_name, label: col.field_name }
        })
        return { label: blocks, items: items }
      })
      return columns
    } else {
      return null
    }
  })()

  const { data: custom_column_data, loading: custom_column_loading } = useQuery(
    CUSTOM_COLUMN,
    {
      variables: {
        table_name: subtype,
      },
      fetchPolicy: "network-only",
      skip: _.isEmpty(subtype) || type !== "Custom Reporting",
    },
  )

  const custom_column_options = (() => {
    if (type === "Custom Reporting" && custom_column_data) {
      const custom_columns = custom_column_data.ReportCustomColumn.map(
        (col) => {
          return { value: col.column_name, label: col.column_name }
        },
      )
      return [{ label: subtype, items: custom_columns }]
    } else {
      return null
    }
  })()

  const {
    data: microservices_column_data,
    loading: microservices_column_loading,
  } = useQuery(GPRA_COLUMN, {
    variables: {
      table_name: subtype,
    },
    fetchPolicy: "network-only",
    skip: _.isEmpty(subtype) || type !== "Microservices Reporting",
  })

  const microservices_column_options = (() => {
    if (type === "Microservices Reporting" && microservices_column_data) {
      const microservices_columns =
        microservices_column_data.ReportMicroservicesColumns.map((col) => {
          return { value: col.column_name, label: col.column_name }
        })
      return [{ label: subtype, items: microservices_columns }]
    } else {
      return null
    }
  })()

  const { data: participant_column_data, loading: participant_column_loading } =
    useQuery(PARTICIPANT_COLUMN, {
      variables: {
        table_name: subtype,
      },
      fetchPolicy: "network-only",
      skip: _.isEmpty(subtype) || type !== PARTICIPANT_REPORT_TYPE,
    })

  const validParticipantSubtypes = [
    "Participant Recovery Characteristics",
    "Participant Engaged Services",
    "Participant Demographics",
    "Participant Contact Information",
  ]

  const participantColumnRenameMap = {
    program_identity_names: "participant_assigned_programs",
    location_names: "participant_assigned_locations",
  }

  const shouldRenameParticipantColumns =
    type === PARTICIPANT_REPORT_TYPE &&
    validParticipantSubtypes.includes(subtype)

  const normalizeParticipantColumnName = (columnName) => {
    if (!shouldRenameParticipantColumns) {
      return columnName
    }

    return participantColumnRenameMap[columnName] || columnName
  }

  const getParticipantFieldValue = (row, columnName) => {
    if (columnName === "participant_assigned_programs") {
      return row[columnName] ?? row.program_identity_names
    }

    if (columnName === "participant_assigned_locations") {
      return row[columnName] ?? row.location_names
    }

    return row[columnName]
  }

  const normalizedParticipantReportColumns = participant_report_column
    ? participant_report_column.map((col) =>
        normalizeParticipantColumnName(col),
      )
    : null

  const selectedParticipantReportColumns =
    normalizedParticipantReportColumns || []

  const participant_column_options = (() => {
    if (type === PARTICIPANT_REPORT_TYPE && participant_column_data) {
      const participant_columns =
        participant_column_data.ReportParticipantColumns.map((col) => {
          const columnName = normalizeParticipantColumnName(col.column_name)
          return { value: columnName, label: columnName }
        })

      return [{ label: subtype, items: participant_columns }]
    }

    return null
  })()

  // Generate reports and download reports section
  const [fetchisr, { data: isrdata, error: isrerror }] = useLazyQuery(ISR, {
    fetchPolicy: "network-only",
  })
  const [fetchisrgroup, { data: isrgroupdata, error: isrgrouperror }] =
    useLazyQuery(ISR_GROUP_SESSION, {
      fetchPolicy: "network-only",
    })
  const [fetchgpra, { data: gpradata, error: gpraerror }] = useLazyQuery(
    GPRA_REPORTS,
    {
      fetchPolicy: "network-only",
    },
  )

  const [fetchos, { data: osdata, error: oserror }] = useLazyQuery(
    INDIANA_OS_REPORT,
    {
      fetchPolicy: "network-only",
    },
  )
  const [fetchac, { data: acdata, error: acerror }] = useLazyQuery(
    INDIANA_ACTIVITY_SESSION_REPORT,
    {
      fetchPolicy: "network-only",
    },
  )
  const [fetchdm, { data: dmdata, error: dmerror }] = useLazyQuery(
    INDIANA_DEMOGRAPHICS_REPORT,
    {
      fetchPolicy: "network-only",
    },
  )
  const [fetchds, { data: dsdata, error: dserror }] = useLazyQuery(
    INDIANA_DIRECT_SERVICE_REPORT,
    {
      fetchPolicy: "network-only",
    },
  )
  const [fetcheg, { data: egdata, error: egerror }] = useLazyQuery(
    INDIANA_ENGAGEMENT_REPORT,
    {
      fetchPolicy: "network-only",
    },
  )
  const [fetchgp, { data: gpdata, error: gperror }] = useLazyQuery(
    INDIANA_GPRA_REPORT,
    {
      fetchPolicy: "network-only",
    },
  )
  const [fetchlr, { data: lrdata, error: lrerror }] = useLazyQuery(
    INDIANA_LIST_REFERRALS_REPORT,
    {
      fetchPolicy: "network-only",
    },
  )
  const [fetchld, { data: lddata, error: lderror }] = useLazyQuery(
    INDIANA_LYFT_DROPOFF_REPORT,
    {
      fetchPolicy: "network-only",
    },
  )
  const [fetchly, { data: lydata, error: lyerror }] = useLazyQuery(
    INDIANA_LYFT_REPORT,
    {
      fetchPolicy: "network-only",
    },
  )
  const [fetchre, { data: redata, error: reerror }] = useLazyQuery(
    INDIANA_REFERRALS_REPORT,
    {
      fetchPolicy: "network-only",
    },
  )
  const [fetchsp, { data: spdata, error: sperror }] = useLazyQuery(
    INDIANA_SUPERVISION_REPORT,
    {
      fetchPolicy: "network-only",
    },
  )
  const [fetchcl, { data: cldata, error: clerror }] = useLazyQuery(
    INDIANA_CONTACT_LOG_REPORT,
    {
      fetchPolicy: "network-only",
    },
  )
  const [fetchReportAsyncExportStatus] = useLazyQuery(
    REPORT_ASYNC_EXPORT_STATUS,
    {
      fetchPolicy: "network-only",
    },
  )
  const [
    fetchprimechracteristics,
    { data: primecharacteristicsdata, error: primecharacteristicserror },
  ] = useLazyQuery(PRIME_CHRACTERISTICS_REPORT, {
    fetchPolicy: "network-only",
  })
  const [
    fetchprimedemographics,
    { data: primedemographicsdata, error: primedemographicserror },
  ] = useLazyQuery(PRIME_DEMOGRAPHICS_REPORT, {
    fetchPolicy: "network-only",
  })
  //New PRIME reporte
  const [
    fetchprimeintakes,
    { data: primeintakesdata, error: primeintakeserror },
  ] = useLazyQuery(PRIME_INTAKES_REPORT, {
    fetchPolicy: "network-only",
  })
  const [
    fetchprimecheckins,
    { data: primecheckinsdata, error: primecheckinserror },
  ] = useLazyQuery(PRIME_CHECK_INS_REPORT, {
    fetchPolicy: "network-only",
  })
  const [fetchprimegpra, { data: primegpradata, error: primegpraerror }] =
    useLazyQuery(PRIME_GPRA_REPORT, {
      fetchPolicy: "network-only",
    })
  //New PRIME ends
  const [
    fetchkioskactivity,
    { data: kioskactivitydata, error: kioskactivityerror },
  ] = useLazyQuery(KIOSK_ACTIVITY_REPORT, {
    fetchPolicy: "network-only",
  })
  const [
    fetchkioskconsent,
    { data: kioskconsentdata, error: kioskconsenterror },
  ] = useLazyQuery(KIOSK_CONSENT_REPORT, {
    fetchPolicy: "network-only",
  })
  const [fetchreferrals, { data: referralsdata, error: referralserror }] =
    useLazyQuery(REFERRALS_REPORT, {
      fetchPolicy: "network-only",
    })
  const [
    fetchbarc10monthly,
    { data: barc10monthlydata, error: barc10monthlyerror },
  ] = useLazyQuery(BARC10_MONTHLY_REPORT, {
    fetchPolicy: "network-only",
  })
  const [
    fetchbarc10individual,
    { data: barc10individualdata, error: barc10individualerror },
  ] = useLazyQuery(BARC10_INDIVIDUAL_REPORT, {
    fetchPolicy: "network-only",
  })
  const [
    fetchtelerecoverysessions,
    { data: telerecoverysessionsdata, error: telerecoverysessionserror },
  ] = useLazyQuery(TELERECOVERY_SESSIONS_REPORT, {
    fetchPolicy: "network-only",
  })
  const [
    fetchparticipantcontact,
    { data: participantcontactdata, error: participantcontacterror },
  ] = useLazyQuery(PARTICIPANT_CONTACT_REPORT, {
    fetchPolicy: "network-only",
  })
  const [
    fetchparticipantdemographics,
    { data: participantdemographicsdata, error: participantcdemographicserror },
  ] = useLazyQuery(PARTICIPANT_DEMOGRAPHICS_REPORT, {
    fetchPolicy: "network-only",
  })
  const [
    fetchparticipantengaged,
    { data: participantengageddata, error: participantengagederror },
  ] = useLazyQuery(PARTICIPANT_ENGAGED_REPORT, {
    fetchPolicy: "network-only",
  })
  const [
    fetchparticipantcharacteristics,
    {
      data: participantcharacteristicsdata,
      error: participantcharacteristicserror,
    },
  ] = useLazyQuery(PARTICIPANT_CHARACTERISTICS_REPORT, {
    fetchPolicy: "network-only",
  })
  const [
    fetchnhmedicaidbilling,
    { data: nhmedicaidbillingdata, error: nhmedicaidbillingerror },
  ] = useLazyQuery(NH_MEDICAID_BILLING_REPORT, {
    fetchPolicy: "network-only",
  })

  const organization_transform_name_join = (() => {
    if (organization_transform_name) {
      const output = organization_transform_name
        .map(([x]) => "'" + x.name + "'")
        .join(",")
      return output
    } else {
      return organization_transform_name
    }
  })()

  const location_transform = (() => {
    if (location_data && location) {
      const output = location.map((x) => {
        const name = location_data.ReportLocation.filter(
          (item) => item.location_id === x,
        ).map((col) => {
          return col.location_name
            .replace("(", "[(]")
            .replace(")", "[)]")
            .replace('"', '\\"')
        })
        return name
      })
      return output
    } else {
      return location
    }
  })()

  const location_transform_name = (() => {
    if (location_transform && location_transform.length !== 0) {
      const output = location_transform.map(([x]) => x).join("|")
      return `"` + output + `"`
    } else {
      return " "
    }
  })()

  const program_transform = (() => {
    if (program_data && program) {
      const output = program.map((x) => {
        const name = program_data.ReportProgram.filter(
          (item) => item.program_identity_id === x,
        ).map((col) => {
          return col.program_id_name
            .replace("(", "[(]")
            .replace(")", "[)]")
            .replace('"', '\\"')
        })
        return name
      })
      return output
    } else {
      return program
    }
  })()

  const program_transform_name = (() => {
    if (program_transform && program_transform.length !== 0) {
      const output = program_transform.map(([x]) => x).join("|")
      return `"` + output + `"`
    } else {
      return " "
    }
  })()

  const program_transform_name_gpra = (() => {
    if (program && program.length !== 0) {
      const output = program_transform.map(([x]) => `"` + x + `"`).join(",")
      return output
    } else {
      return " "
    }
  })()

  const status_transform = (() => {
    if (status && status.length !== 0) {
      return status.map((x) => `"` + x + `"`).join(",")
    } else {
      return " "
    }
  })()

  const staff_transform_by_name = (() => {
    if (staff && staff.length !== 0) {
      return staff.map((x) => `"` + x + `"`).join(",")
    } else {
      return " "
    }
  })()

  const staff_transform_by_id = (() => {
    if (staff && staff.length !== 0 && staff_data) {
      const ids = staff.flatMap((name) => {
        const match = staff_data.ReportStaff.find((item) => item.staff === name)
        return match ? [`"${match.staff_id}"`] : []
      })
      return ids.length !== 0 ? ids.join(",") : " "
    }
    return " "
  })()

  const facilitating_staff_transform_by_id = (() => {
    if (facilitating_staff && facilitating_staff.length !== 0) {
      return facilitating_staff.map((x) => `"` + x + `"`).join(",")
    } else {
      return " "
    }
  })()

  const facilitating_staff_transform_by_name = (() => {
    if (facilitating_staff && facilitating_staff.length !== 0 && staff_data) {
      const names = facilitating_staff.flatMap((id) => {
        const match = staff_data.ReportStaff.find(
          (item) => item.staff_id === id,
        )
        return match ? [`"${match.staff}"`] : []
      })
      return names.length !== 0 ? names.join(",") : " "
    }
    return " "
  })()

  const column_transform = (() => {
    if (column) {
      return column.map((x) => "'" + x + "'").join(",")
    } else {
      return column
    }
  })()

  /**
   * Selected reporting fields for async CSV column filtering.
   * Organization Reporting keeps its legacy quoted-string `column_transform`.
   * Custom / Microservices / Participant pass a string array to the server.
   */
  const selectedColumnsForRequest = (() => {
    if (type === ORGANIZATION_REPORTING_TYPE) {
      return null
    }
    if (type === "Custom Reporting" && Array.isArray(custom_report_column)) {
      return custom_report_column.filter(Boolean)
    }
    if (
      type === MICROSERVICES_REPORTING_TYPE &&
      Array.isArray(microservices_report_column)
    ) {
      return microservices_report_column.filter(Boolean)
    }
    if (
      type === PARTICIPANT_REPORT_TYPE &&
      Array.isArray(normalizedParticipantReportColumns)
    ) {
      return normalizedParticipantReportColumns.filter(Boolean)
    }
    return null
  })()

  /**
   * GraphQL query hook for fetching organization reports data
   * Uses lazy query to control when the data is fetched
   */
  const [
    fetchorganizationreports,
    { data: organization_reports_data, error: organization_reports_error },
  ] = useLazyQuery(ORGANIZATION_REPORTS, { fetchPolicy: "network-only" })

  /**
   * Transforms the raw organization reports data into a structured format
   * - Maps the data to a consistent object structure
   * - Handles special numeric fields (activity_distributed, activity_attendance_hr, etc.)
   * - Parses and processes the fields_json data
   * @returns {Array|null} Transformed data array or null if no data available
   */

  const organization_reports_data_transform = (() => {
    if (organization_reports_data?.ReportOrganizationReports?.results) {
      const output =
        organization_reports_data.ReportOrganizationReports.results.map(
          (row) => {
            // Base object with standard fields
            let rowObject = {
              organization_name: row.organization_name,
              participant_membership_status: row.participant_membership_status,
              participant_assigned_locations:
                row.participant_assigned_locations,
              participant_assigned_programs: row.participant_assigned_programs,
              ...(subtype !== "Activity"
                ? { assigned_staff_id: row.assigned_staff_id }
                : {}),
              participant_membership_id: row.participant_membership_id,
              participant_alias: row.participant_alias,
              participant_name_first: row.participant_name_first,
              participant_name_last: row.participant_name_last,
              participant_email_account: row.participant_email_account,
              facilitating_staff_alias_id: row.facilitating_staff_alias_id,
              facilitating_staff_name_first: row.facilitating_staff_name_first,
              facilitating_staff_name_last: row.facilitating_staff_name_last,
              session_id: row.session_id,
            }

            // Process additional fields from fields_json
            const fieldsJsonObj = JSON.parse(row.fields_json || "{}")
            for (const [key, value] of Object.entries(fieldsJsonObj)) {
              // Convert specific fields to numbers if they contain numeric values
              if (NUMERIC_FIELDS.includes(key) && value !== null) {
                rowObject[key] = Number(value)
              } else if (
                // ERR-2004: Document Participant File
                key === DOCUMENT_PARTICIPANT_FILE_KEY &&
                value !== null
              ) {
                rowObject[key] = getFilename(value) || value
              } else {
                rowObject[key] = value
              }
            }

            return rowObject
          },
        )

      return output
    } else {
      return null
    }
  })()

  const ASYNC_POLL_INTERVAL_MS = 5000
  const ASYNC_SOFT_TIMEOUT_MS = 5 * 60 * 1000
  const ASYNC_HARD_TIMEOUT_MS = 30 * 60 * 1000

  const stopAsyncExportPolling = () => {
    pollActiveRef.current = false
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }
    if (elapsedIntervalRef.current) {
      clearInterval(elapsedIntervalRef.current)
      elapsedIntervalRef.current = null
    }
  }

  const clearAsyncExportPolling = () => {
    stopAsyncExportPolling()
  }

  const startAsyncExportElapsedTimer = (pollStartedAt) => {
    if (elapsedIntervalRef.current) {
      clearInterval(elapsedIntervalRef.current)
    }
    const tickElapsed = () => {
      const elapsedSeconds = Math.floor((Date.now() - pollStartedAt) / 1000)
      setAsyncExportInfo((prev) => {
        if (!prev || !pollActiveRef.current) return prev
        if (prev.elapsedSeconds === elapsedSeconds) return prev
        return { ...prev, elapsedSeconds }
      })
    }
    tickElapsed()
    elapsedIntervalRef.current = setInterval(tickElapsed, 1000)
  }

  const pollReportExportStatus = (jobId, reportName) => {
    const pollStartedAt = Date.now()

    clearAsyncExportPolling()
    softTimeoutToastShownRef.current = false
    pollActiveRef.current = true
    startAsyncExportElapsedTimer(pollStartedAt)

    setAsyncExportInfo({
      status: "PENDING",
      jobId,
      reportName,
      downloadUrl: null,
      expiresAt: null,
      elapsedSeconds: 0,
      softTimeoutReached: false,
      estimatedRows: null,
      rowCount: null,
      fileSizeBytes: null,
      message: "Large report detected. Preparing compressed export file...",
    })

    const poll = async () => {
      if (!pollActiveRef.current) return

      const elapsed = Date.now() - pollStartedAt

      if (elapsed >= ASYNC_HARD_TIMEOUT_MS) {
        stopAsyncExportPolling()
        setAsyncExportInfo((prev) => ({
          ...(prev || {}),
          status: "TIMEOUT",
          jobId,
          reportName,
          softTimeoutReached: true,
          message: `Stopped checking after ${Math.round(ASYNC_HARD_TIMEOUT_MS / 60000)} minutes. The export may still complete on the server (job ${jobId}). You can generate the report again later to check for a download.`,
        }))
        return
      }

      if (
        elapsed >= ASYNC_SOFT_TIMEOUT_MS &&
        !softTimeoutToastShownRef.current
      ) {
        softTimeoutToastShownRef.current = true
        setAsyncExportInfo((prev) => ({
          ...(prev || {}),
          softTimeoutReached: true,
        }))
        message.current.show({
          severity: "warn",
          detail:
            "This export is taking longer than usual. We are still checking for your download.",
          life: 8000,
        })
      }

      try {
        const { data } = await fetchReportAsyncExportStatus({
          variables: { job_id: jobId },
        })

        if (!pollActiveRef.current) return

        const statusPayload = data?.ReportAsyncExportStatus
        if (!statusPayload) {
          throw new Error("Missing export status payload")
        }

        if (statusPayload.status === "READY" && statusPayload.download_url) {
          stopAsyncExportPolling()
          setAsyncExportInfo({
            status: "READY",
            jobId,
            reportName,
            downloadUrl: statusPayload.download_url,
            downloadFilename: statusPayload.download_filename || null,
            expiresAt: statusPayload.expires_at,
            rowCount: statusPayload.row_count ?? null,
            estimatedRows: null,
            fileSizeBytes: statusPayload.file_size_bytes ?? null,
            softTimeoutReached: false,
            message:
              statusPayload.message ||
              "Export is ready. The file will be deleted after 7 days.",
          })
          return
        }

        if (
          statusPayload.status === "FAILED" ||
          statusPayload.status === "EXPIRED" ||
          statusPayload.status === "NOT_FOUND"
        ) {
          stopAsyncExportPolling()
          setAsyncExportInfo({
            status: statusPayload.status,
            jobId,
            reportName,
            downloadUrl: null,
            expiresAt: statusPayload.expires_at,
            rowCount: statusPayload.row_count ?? null,
            estimatedRows: null,
            message:
              statusPayload.message ||
              "The export could not be completed. Please try again.",
          })
          return
        }

        setAsyncExportInfo((prev) => ({
          ...(prev || {}),
          status: statusPayload.status || "PENDING",
          jobId,
          reportName,
          estimatedRows:
            statusPayload.estimated_rows ?? prev?.estimatedRows ?? null,
          rowCount: statusPayload.row_count ?? prev?.rowCount ?? null,
          message:
            statusPayload.message ||
            "Large report detected. Preparing compressed export file...",
        }))
      } catch (error) {
        if (!pollActiveRef.current) return
        setAsyncExportInfo((prev) => ({
          ...(prev || {}),
          status: prev?.status === "PROCESSING" ? "PROCESSING" : "PENDING",
          jobId,
          reportName,
          message: "Still preparing your export file...",
        }))
      }

      if (pollActiveRef.current) {
        pollTimeoutRef.current = setTimeout(poll, ASYNC_POLL_INTERVAL_MS)
      }
    }

    void poll()
  }

  const fetchReportWithAsyncFallback = (fetchFn, variables, reportName) => {
    clearAsyncExportPolling()
    setAsyncExportInfo(null)

    fetchFn({
      variables,
      onCompleted: (data) => {
        const payload = data?.[reportName]
        if (payload?.isAsync && payload?.jobId) {
          setLoading(false)
          setClear(true)
          pollReportExportStatus(payload.jobId, reportName)
          return
        }

        setLoading(false)
        setClear(true)
      },
      onError: (error) => {
        LogError("Error fetching report:", { ...error })
        setLoading(false)
        setClear(true)
        message.current.show({
          severity: "error",
          detail:
            "Unable to generate the report. Please try again." +
            (error.message ? ` Error: ${error.message}` : ""),
          life: 8000,
        })
      },
    })
  }

  useEffect(() => {
    return () => {
      clearAsyncExportPolling()
    }
  }, [])

  /**
   * Exports the current data table view to CSV format
   * Handles large datasets with progress indicator
   * @param {boolean} selectionOnly - If true, only exports selected rows
   */
  const exportCSV = (selectionOnly) => {
    const value = dtfilter ? dtfilter : dt.current.props.value
    const totalRows = value.length

    // For large datasets, show warning and use optimized export
    if (totalRows > 10000) {
      const shouldContinue = window.confirm(
        `You're trying to export ${totalRows.toLocaleString()} rows to CSV. ` +
          `This may take some time. Continue?`,
      )

      if (!shouldContinue) {
        return
      }

      // Show progress for large CSV exports
      const progressDiv = document.createElement("div")
      progressDiv.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: white;
        border: 2px solid #28a745;
        border-radius: 10px;
        padding: 20px;
        z-index: 9999;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        min-width: 350px;
      `
      progressDiv.innerHTML = `
        <div style="text-align: center;">
          <h4>Processing CSV Export...</h4>
          <div id="csv-progress-text">Processing rows 0 of ${totalRows.toLocaleString()}</div>
          <div style="width: 300px; height: 20px; background: #f0f0f0; border-radius: 10px; margin: 10px 0;">
            <div id="csv-progress-bar" style="width: 0%; height: 100%; background: #28a745; border-radius: 10px; transition: width 0.3s;"></div>
          </div>
          <div id="csv-progress-percentage" style="font-size: 12px; color: #666;">0%</div>
        </div>
      `
      document.body.appendChild(progressDiv)

      // Use setTimeout to allow UI to update
      setTimeout(() => {
        try {
          dt.current.exportCSV({ selectionOnly }, "exportName")
          document.body.removeChild(progressDiv)
        } catch (error) {
          LogError("Error exporting CSV:", error)
          document.body.removeChild(progressDiv)
          alert("Error exporting CSV. Please try again.")
        }
      }, 100)
    } else {
      // For smaller datasets, use the original approach
      dt.current.exportCSV({ selectionOnly }, "exportName")
    }
  }

  /**
   * Saves the provided buffer as an Excel file
   * @param {Buffer} buffer - The data buffer to save
   * @param {string} fileName - The name of the file to save
   */
  const saveAsExcelFile = (buffer, fileName) => {
    import("file-saver").then((module) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
        let EXCEL_EXTENSION = ".xlsx"
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        })

        module.default.saveAs(data, fileName + EXCEL_EXTENSION)
      }
    })
  }

  /**
   * Exports the current data table view to Excel format
   * Handles different report types and column ordering with chunked processing for large datasets
   */
  const exportExcel = () => {
    import("xlsx").then((xlsx) => {
      const value = dtfilter ? dtfilter : dt.current.props.value

      // For large datasets, use chunked processing
      // Adjust chunk size based on data complexity and system capabilities
      const getChunkSize = () => {
        const totalRows = value.length
        const totalColumns =
          type === "Custom Reporting"
            ? custom_report_column?.length || 0
            : type === "Microservices Reporting"
              ? microservices_report_column?.length || 0
              : type === PARTICIPANT_REPORT_TYPE
                ? participant_report_column?.length || 0
                : column?.length || 0

        // Chrome-specific optimizations: Use smaller chunks to prevent memory issues
        const isChrome =
          /Chrome/.test(navigator.userAgent) &&
          /Google Inc/.test(navigator.vendor)

        if (isChrome) {
          // Chrome tends to have memory issues with large chunks
          if (totalRows > 50000 && totalColumns > 50) {
            return 250
          } else if (totalRows > 25000 && totalColumns > 30) {
            return 500
          } else {
            return 750
          }
        } else {
          // For other browsers, use original chunk sizes
          if (totalRows > 50000 && totalColumns > 50) {
            return 500
          } else if (totalRows > 25000 && totalColumns > 30) {
            return 750
          } else {
            return 1000
          }
        }
      }

      const CHUNK_SIZE = getChunkSize()
      const totalRows = value.length

      // For extremely large datasets (>50k rows), suggest server-side export
      if (totalRows > 50000) {
        const shouldUseServerSide = window.confirm(
          `You're trying to export ${totalRows.toLocaleString()} rows. This may cause browser performance issues. ` +
            `Would you like to use server-side export instead? (Recommended for datasets >50k rows)`,
        )

        if (shouldUseServerSide) {
          // TODO: Implement server-side export endpoint
          alert(
            "Server-side export feature is not yet implemented. Please contact your administrator.",
          )
          return
        }
      }

      if (totalRows > CHUNK_SIZE) {
        // Show progress indicator
        const progressDiv = document.createElement("div")
        progressDiv.style.cssText = `
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: white;
          border: 2px solid #007bff;
          border-radius: 10px;
          padding: 20px;
          z-index: 9999;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          min-width: 350px;
        `
        progressDiv.innerHTML = `
          <div style="text-align: center;">
            <h4>Processing Excel Export...</h4>
            <div id="progress-text">Processing rows 0 of ${totalRows.toLocaleString()}</div>
            <div style="width: 300px; height: 20px; background: #f0f0f0; border-radius: 10px; margin: 10px 0;">
              <div id="progress-bar" style="width: 0%; height: 100%; background: #007bff; border-radius: 10px; transition: width 0.3s;"></div>
            </div>
            <div id="progress-percentage" style="font-size: 12px; color: #666;">0%</div>
            <button id="cancel-export" style="margin-top: 10px; padding: 5px 15px; background: #dc3545; color: white; border: none; border-radius: 5px; cursor: pointer;">Cancel</button>
          </div>
        `
        document.body.appendChild(progressDiv)

        // Add cancel functionality
        let isCancelled = false
        document
          .getElementById("cancel-export")
          .addEventListener("click", () => {
            isCancelled = true
            document.body.removeChild(progressDiv)
            if (window.excelWorksheet) {
              delete window.excelWorksheet
            }
          })

        // Process data in chunks
        const processChunk = (startIndex) => {
          if (isCancelled) return

          const endIndex = Math.min(startIndex + CHUNK_SIZE, totalRows)
          const chunk = value.slice(startIndex, endIndex)

          // Update progress
          const progressText = document.getElementById("progress-text")
          const progressBar = document.getElementById("progress-bar")
          const progressPercentage = document.getElementById(
            "progress-percentage",
          )
          const progress = Math.round((endIndex / totalRows) * 100)

          if (progressText)
            progressText.textContent = `Processing rows ${endIndex.toLocaleString()} of ${totalRows.toLocaleString()}`
          if (progressBar) progressBar.style.width = `${progress}%`
          if (progressPercentage)
            progressPercentage.textContent = `${progress}%`

          try {
            // Process this chunk
            const chunkData = chunk.map((row) => {
              let rowObject = {}

              if (type === ORGANIZATION_REPORTING_TYPE) {
                if (dtreorder) {
                  dtreorder.columns.forEach((col) => {
                    rowObject[col.props.field] = row[col.props.field]
                  })
                } else {
                  rowObject["organization_name"] = row["organization_name"]
                  if (subtype !== "Activity") {
                    rowObject["assigned_staff_id"] = row["assigned_staff_id"]
                    rowObject["facilitating_staff_alias_id"] =
                      row["facilitating_staff_alias_id"]
                    rowObject["facilitating_staff_name_first"] =
                      row["facilitating_staff_name_first"]
                    rowObject["facilitating_staff_name_last"] =
                      row["facilitating_staff_name_last"]
                    rowObject["participant_membership_id"] =
                      row["participant_membership_id"]
                    rowObject["participant_assigned_locations"] =
                      row["participant_assigned_locations"]
                    rowObject["participant_assigned_programs"] =
                      row["participant_assigned_programs"]
                    rowObject["participant_alias"] = row["participant_alias"]
                    rowObject["participant_name_first"] =
                      row["participant_name_first"]
                    rowObject["participant_name_last"] =
                      row["participant_name_last"]
                    rowObject["participant_email_account"] =
                      row["participant_email_account"]
                    rowObject["participant_membership_status"] =
                      row["participant_membership_status"]
                  } else {
                    rowObject["facilitating_staff_alias_id"] =
                      row["facilitating_staff_alias_id"]
                    rowObject["facilitating_staff_name_first"] =
                      row["facilitating_staff_name_first"]
                    rowObject["facilitating_staff_name_last"] =
                      row["facilitating_staff_name_last"]
                  }

                  if (subtype === "Session") {
                    rowObject["session_id"] = row["session_id"]
                  }

                  column.forEach((col) => {
                    rowObject[col] = row[col]
                    if (col === "supervision__staff_membership_id") {
                      rowObject["supervision__staff_name"] =
                        row["supervision__staff_name"]
                    }
                  })
                }
              } else if (type === "Custom Reporting") {
                if (dtreorder) {
                  dtreorder.columns.forEach((col) => {
                    rowObject[col.props.field] = row[col.props.field]
                  })
                } else {
                  custom_report_column.forEach((col) => {
                    rowObject[col] = row[col]
                  })
                }
              } else if (type === "Microservices Reporting") {
                if (dtreorder) {
                  dtreorder.columns.forEach((col) => {
                    rowObject[col.props.field] = row[col.props.field]
                  })
                } else {
                  microservices_report_column.forEach((col) => {
                    rowObject[col] = row[col]
                  })
                }
              } else if (type === PARTICIPANT_REPORT_TYPE) {
                if (dtreorder) {
                  dtreorder.columns.forEach((col) => {
                    rowObject[col.props.field] = getParticipantFieldValue(
                      row,
                      col.props.field,
                    )
                  })
                } else {
                  selectedParticipantReportColumns.forEach((col) => {
                    rowObject[col] = getParticipantFieldValue(row, col)
                  })
                }
              }

              return rowObject
            })

            // Add chunk to worksheet
            if (startIndex === 0) {
              // First chunk - create worksheet
              const worksheet = xlsx.utils.json_to_sheet(chunkData)
              window.excelWorksheet = worksheet
            } else {
              // Subsequent chunks - append to existing worksheet
              const range = xlsx.utils.decode_range(
                window.excelWorksheet["!ref"],
              )

              // Convert chunkData from array of objects to array of arrays for sheet_add_aoa
              const chunkDataAsArrays = chunkData.map((row) => {
                // Ensure all values are valid and handle null/undefined values
                return Object.values(row).map((value) => {
                  if (value === null || value === undefined) {
                    return ""
                  }
                  // Convert to string to avoid any potential issues with complex objects
                  return String(value)
                })
              })

              try {
                const newRange = xlsx.utils.decode_range(
                  xlsx.utils.sheet_add_aoa(
                    window.excelWorksheet,
                    chunkDataAsArrays,
                    {
                      origin: `A${range.e.r + 2}`,
                    },
                  )["!ref"],
                )
                window.excelWorksheet["!ref"] = xlsx.utils.encode_range({
                  s: { r: 0, c: 0 },
                  e: { r: newRange.e.r, c: newRange.e.c },
                })
              } catch (sheetError) {
                LogError("Error adding chunk to worksheet:", sheetError)
                LogError("chunkDataAsArrays:", chunkDataAsArrays)
                throw sheetError
              }
            }

            // Continue with next chunk or finish
            if (endIndex < totalRows) {
              // Chrome-specific: Add longer delay and force garbage collection hint
              const isChrome =
                /Chrome/.test(navigator.userAgent) &&
                /Google Inc/.test(navigator.vendor)
              const delay = isChrome ? 50 : 10 // Longer delay for Chrome

              setTimeout(() => {
                // Force garbage collection hint for Chrome
                if (isChrome && window.gc) {
                  try {
                    window.gc()
                  } catch (e) {
                    // Ignore if gc is not available
                  }
                }
                processChunk(endIndex)
              }, delay)
            } else {
              // Finish processing
              // Validate worksheet structure
              if (!window.excelWorksheet || !window.excelWorksheet["!ref"]) {
                throw new Error(
                  "Invalid worksheet structure - missing worksheet or range reference",
                )
              }

              // Ensure the worksheet has valid data
              const range = xlsx.utils.decode_range(
                window.excelWorksheet["!ref"],
              )
              if (range.e.r < 0 || range.e.c < 0) {
                throw new Error("Invalid worksheet range - negative dimensions")
              }

              const workbook = {
                Sheets: { data: window.excelWorksheet },
                SheetNames: ["data"],
              }

              try {
                // Chrome-specific optimization: Use setTimeout to allow garbage collection
                setTimeout(() => {
                  try {
                    const excelBuffer = xlsx.write(workbook, {
                      bookType: "xlsx",
                      type: "array",
                    })

                    // Clean up
                    delete window.excelWorksheet
                    document.body.removeChild(progressDiv)

                    // Save file
                    const fileName =
                      type === ORGANIZATION_REPORTING_TYPE ? template : subtype
                    saveAsExcelFile(excelBuffer, fileName)
                  } catch (writeError) {
                    LogError("Error writing Excel file:", writeError)
                    document.body.removeChild(progressDiv)
                    if (window.excelWorksheet) {
                      delete window.excelWorksheet
                    }
                    alert(
                      "Error creating Excel file. Please try again or contact support.",
                    )
                  }
                }, 0)
              } catch (writeError) {
                LogError("Error in setTimeout:", writeError)
                document.body.removeChild(progressDiv)
                if (window.excelWorksheet) {
                  delete window.excelWorksheet
                }
                alert(
                  "Error creating Excel file. Please try again or contact support.",
                )
              }
            }
          } catch (error) {
            LogError("Error processing Excel export:", error)
            document.body.removeChild(progressDiv)
            if (window.excelWorksheet) {
              delete window.excelWorksheet
            }
            alert(
              "Error processing Excel export. Please try again or contact support.",
            )
          }
        }

        // Start processing
        processChunk(0)
      } else {
        // For smaller datasets, use the original approach
        const data = value.map((row) => {
          let rowObject = {}

          if (type === ORGANIZATION_REPORTING_TYPE) {
            if (dtreorder) {
              dtreorder.columns.forEach((col) => {
                rowObject[col.props.field] = row[col.props.field]
              })
            } else {
              rowObject["organization_name"] = row["organization_name"]
              if (subtype !== "Activity") {
                rowObject["assigned_staff_id"] = row["assigned_staff_id"]
                rowObject["facilitating_staff_alias_id"] =
                  row["facilitating_staff_alias_id"]
                rowObject["facilitating_staff_name_first"] =
                  row["facilitating_staff_name_first"]
                rowObject["facilitating_staff_name_last"] =
                  row["facilitating_staff_name_last"]
                rowObject["participant_membership_id"] =
                  row["participant_membership_id"]
                rowObject["participant_assigned_locations"] =
                  row["participant_assigned_locations"]
                rowObject["participant_assigned_programs"] =
                  row["participant_assigned_programs"]
                rowObject["participant_alias"] = row["participant_alias"]
                rowObject["participant_name_first"] =
                  row["participant_name_first"]
                rowObject["participant_name_last"] =
                  row["participant_name_last"]
                rowObject["participant_email_account"] =
                  row["participant_email_account"]
                rowObject["participant_membership_status"] =
                  row["participant_membership_status"]
              } else {
                rowObject["facilitating_staff_alias_id"] =
                  row["facilitating_staff_alias_id"]
                rowObject["facilitating_staff_name_first"] =
                  row["facilitating_staff_name_first"]
                rowObject["facilitating_staff_name_last"] =
                  row["facilitating_staff_name_last"]
              }

              if (subtype === "Session") {
                rowObject["session_id"] = row["session_id"]
              }

              column.forEach((col) => {
                rowObject[col] = row[col]
                if (col === "supervision__staff_membership_id") {
                  rowObject["supervision__staff_name"] =
                    row["supervision__staff_name"]
                }
              })
            }
          } else if (type === "Custom Reporting") {
            if (dtreorder) {
              dtreorder.columns.forEach((col) => {
                rowObject[col.props.field] = row[col.props.field]
              })
            } else {
              custom_report_column.forEach((col) => {
                rowObject[col] = row[col]
              })
            }
          } else if (type === "Microservices Reporting") {
            if (dtreorder) {
              dtreorder.columns.forEach((col) => {
                rowObject[col.props.field] = row[col.props.field]
              })
            } else {
              microservices_report_column.forEach((col) => {
                rowObject[col] = row[col]
              })
            }
          } else if (type === PARTICIPANT_REPORT_TYPE) {
            if (dtreorder) {
              dtreorder.columns.forEach((col) => {
                rowObject[col.props.field] = getParticipantFieldValue(
                  row,
                  col.props.field,
                )
              })
            } else {
              selectedParticipantReportColumns.forEach((col) => {
                rowObject[col] = getParticipantFieldValue(row, col)
              })
            }
          }

          return rowObject
        })
        const worksheet = xlsx.utils.json_to_sheet(data)
        const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] }
        const excelBuffer = xlsx.write(workbook, {
          bookType: "xlsx",
          type: "array",
        })
        const fileName =
          type === ORGANIZATION_REPORTING_TYPE ? template : subtype
        saveAsExcelFile(excelBuffer, fileName)
      }
    })
  }

  /**
   * Header component containing export buttons for CSV and Excel
   * Provides data export functionality with visual feedback
   */
  const headers = (
    <div className={styles.download_buttons}>
      <Button
        type="button"
        label="CSV"
        icon="pi pi-file"
        onClick={() => exportCSV(false)}
        className="mr-2"
        data-pr-tooltip="CSV"
      />
      <Button
        type="button"
        label="Excel"
        icon="pi pi-file-excel"
        onClick={() => exportExcel()}
        className="mr-2 p-button-success"
        data-pr-tooltip="XLS"
      />
      {/* PDF export button currently disabled */}
      {/* <Button type="button" icon="pi pi-file-pdf" onClick={exportPdf} className="mr-2 p-button-warning" data-pr-tooltip="PDF" /> */}
    </div>
  )

  /**
   * Custom day content renderer for the date picker
   * Adds visual indicators for weekend days
   * @param {Date} day - The date to render
   * @returns {JSX.Element} Customized day content
   */
  const customDayContent = (day) => {
    if (isWeekend(day)) {
      return (
        <div>
          <div
            style={{
              height: "5px",
              width: "5px",
              borderRadius: "100%",
              background: "orange",
              position: "absolute",
              top: 2,
              right: 2,
            }}
          />
          <span>{format(day, "d")}</span>
        </div>
      )
    }
  }

  /**
   * Renders the static range label for date selection
   * @returns {JSX.Element} CustomStaticRangeLabelContent component
   */
  const renderStaticRangeLabel = () => (
    <CustomStaticRangeLabelContent text={"This is for last [-] days: "} />
  )

  /**
   * Custom component for displaying static range labels with live updates
   * Updates the display every second to show current date/time
   */
  class CustomStaticRangeLabelContent extends React.Component {
    constructor(props) {
      super(props)

      this.state = {
        currentDateString: Date(),
      }

      // Set up interval to update the display every second
      this.intervalId = setInterval(() => {
        this.setState({
          currentDateString: Date(),
        })
      }, 1000)
    }

    /**
     * Cleanup interval on component unmount
     */
    componentWillUnmount() {
      if (this.intervalId) {
        clearInterval(this.intervalId)
      }
    }

    /**
     * CustomStaticRangeLabelContent component render method
     * Displays the current date/time with the provided text
     * @returns {JSX.Element} Formatted date/time display
     */
    render() {
      const { currentDateString } = this.state
      const { text } = this.props

      return (
        <span>
          <i>{text}</i>
          <br />
          <span className={"random-date-string"}>
            <b>{currentDateString}</b>
          </span>
        </span>
      )
    }
  }

  // Section panel header template
  const sectionPanelTemplate = (options, title) => {
    const className = `${options.className} justify-content-start`
    return (
      <div className={className}>
        <strong>{title}</strong>
      </div>
    )
  }

  const renderLoadingLabel = (label, isLoading) => (
    <div className={styles.filterLabelRow}>
      <strong>{label}</strong>
      {isLoading ? (
        <i
          className={`pi pi-spin pi-spinner ${styles.filterLoadingIcon}`}
          aria-hidden="true"
        />
      ) : null}
    </div>
  )

  useEffect(() => {
    const defaultHelpText = {
      location: LOCATION_FILTER_HELPTEXT_DEFAULT,
      program: PROGRAM_FILTER_HELPTEXT_DEFAULT,
      participantStatus: PARTICIPANT_STATUS_FILTER_HELPTEXT_DEFAULT,
      assignedStaff: ASSIGNED_STAFF_FILTER_HELPTEXT_DEFAULT,
      facilitatingStaff: FACILITATING_STAFF_FILTER_HELPTEXT_DEFAULT,
    }

    let nextHelpText = defaultHelpText

    switch (type) {
      case ORGANIZATION_REPORTING_TYPE:
        nextHelpText = {
          ...defaultHelpText,
          location: "Filter by session/activity/form location(s)",
          program: "Filter by session/activity/form program(s)",
          facilitatingStaff: "Filter by session/activity/form author(s)",
        }
        break

      case MICROSERVICES_REPORTING_TYPE: {
        const locationHelpBySubtype = {
          [KIOSK_CHECK_IN_ACTIVITY_REPORT_SUBTYPE]: "Filter by Kiosk Location",
          [REFERRALS_REPORT_SUBTYPE]: "Filter by session location(s)",
          [ASSESSMENTS_BARC_10_REPORT_SUBTYPE]: "Filter by session location(s)",
        }
        const programHelpBySubtype = {
          [REFERRALS_REPORT_SUBTYPE]: "Filter by session program(s)",
        }
        nextHelpText = {
          ...defaultHelpText,
          location: locationHelpBySubtype[subtype] ?? defaultHelpText.location,
          program: programHelpBySubtype[subtype] ?? defaultHelpText.program,
          facilitatingStaff: "Filter by session/activity/form author(s)",
        }
        break
      }

      case CUSTOM_REPORTING_TYPE:
      case PARTICIPANT_REPORT_TYPE:
        nextHelpText = {
          ...defaultHelpText,
        }
        break

      default:
        break
    }

    setLocationFilterHelpText(nextHelpText.location)
    setProgramFilterHelpText(nextHelpText.program)
    setParticipantStatusFilterHelpText(nextHelpText.participantStatus)
    setAssignedStaffFilterHelpText(nextHelpText.assignedStaff)
    setFacilitatingStaffFilterHelpText(nextHelpText.facilitatingStaff)
  }, [type, subtype])

  function clearReportFilters() {
    setLocation(null)
    setProgram(null)
    setStatus(null)
    setStaff(null)
    setFacilitatingStaff(null)
  }

  // Predefine report type checks for filter visibility logic
  const isActivityReport =
    type === ORGANIZATION_REPORTING_TYPE && subtype === "Activity"
  const isParticipantReport = type === PARTICIPANT_REPORT_TYPE
  const isGpraReport =
    type === MICROSERVICES_REPORTING_TYPE && subtype === GPRA_REPORT_SUBTYPE
  const isKioskActivityReport =
    type === MICROSERVICES_REPORTING_TYPE &&
    subtype === KIOSK_CHECK_IN_ACTIVITY_REPORT_SUBTYPE
  const isBarc10MonthlyReport =
    type === MICROSERVICES_REPORTING_TYPE &&
    subtype === ASSESSMENTS_BARC_10_MONTHLY_REPORT_SUBTYPE
  const isBarc10Report =
    type === MICROSERVICES_REPORTING_TYPE &&
    subtype === ASSESSMENTS_BARC_10_REPORT_SUBTYPE

  // Determine whether to hide specific filters based on report type
  function shouldHideFilter(filter) {
    switch (filter) {
      case "location":
        return isGpraReport || isBarc10MonthlyReport
      case "program":
        return isKioskActivityReport || isBarc10MonthlyReport || isBarc10Report
      case "participant_status":
        return (
          isActivityReport ||
          isGpraReport ||
          isKioskActivityReport ||
          isBarc10MonthlyReport
        )
      case "assigned_staff":
        return (
          isActivityReport ||
          isGpraReport ||
          isKioskActivityReport ||
          isBarc10MonthlyReport
        )
      case "facilitating_staff":
        return (
          isParticipantReport ||
          isGpraReport ||
          isKioskActivityReport ||
          isBarc10MonthlyReport
        )
      default:
        return false
    }
  }

  // Effect to clear specific filters when report type changes
  useEffect(() => {
    if (shouldHideFilter("assigned_staff")) setStaff(null)
    if (shouldHideFilter("participant_status")) setStatus(null)
    if (shouldHideFilter("facilitating_staff")) setFacilitatingStaff(null)
    if (shouldHideFilter("location")) setLocation(null)
    if (shouldHideFilter("program")) setProgram(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isActivityReport,
    isParticipantReport,
    isGpraReport,
    isKioskActivityReport,
    isBarc10MonthlyReport,
    isBarc10Report,
  ])

  // Effect to reset filters when the selected organization changes
  useEffect(() => {
    setLocation(null)
    setProgram(null)
    setStatus(null)
    setStaff(null)
    setFacilitatingStaff(null)
  }, [organization])

  useEffect(() => {
    const startDate = date_range[0].startDate
    const endDate = date_range[0].endDate
    const maxEndDate = addYears(startDate, MAX_REPORT_DATE_RANGE_YEARS)

    // Keep reporting queries bounded; product requirement allows up to 10 years.
    if (endDate > maxEndDate) {
      setDateRange([
        {
          ...date_range[0],
          endDate: maxEndDate,
        },
      ])
    }
  }, [date_range])

  return (
    <>
      <Toast ref={message} />
      <PageSidebarContent>
        <SidebarDashboardSupervisor />
      </PageSidebarContent>

      <div className="my-dashboard-wrapper">
        <h1>Organization Reports</h1>

        {/* Help-text message */}
        <div>
          <p className={styles.description}>
            Various filters can be set to modify the data in report selections.
          </p>
          <p className={styles.description}>
            Programs, locations, status, and staff are specific to the
            participants appearing in reports, with the exception of activity
            reports, which are organization-specific.
          </p>
        </div>

        <div className={styles.box}>
          {/* Left panel */}
          <Panel
            headerTemplate={(options) =>
              sectionPanelTemplate(options, "Report Selections")
            }
            className={styles.mypanel}
          >
            <div className={styles.filter}>
              <div>
                {renderLoadingLabel("Organizations:", organization_loading)}
                <MultiSelect
                  className={styles.selectInput}
                  id="organization"
                  value={organization}
                  options={organization_options}
                  onChange={(e) => {
                    setOrganization(e.target.value)
                    setType(null)
                    setSubtype(null)
                    setTemplate(null)
                    setBlock(null)
                    setColumn(null)
                    setCustomReportColumn(null)
                    setMicroServicesReportColumn(null)
                    setParticipantReportColumn(null)
                  }}
                  maxSelectedLabels={1}
                  showClear
                  filter
                  placeholder="Select organizations"
                  optionLabel="label"
                  optionGroupLabel="label"
                  optionGroupChildren="items"
                  scrollHeight="min(60vh, 500px)"
                  loading={organization_loading}
                />
              </div>

              {organization !== null && organization !== undefined ? (
                <div>
                  <strong>Select type of report:</strong>
                  <Dropdown
                    className={styles.selectInput}
                    id="type"
                    value={type}
                    options={report_type}
                    onChange={(e) => {
                      // Clear downstream report selections
                      setType(e.target.value)
                      setSubtype(null)
                      setTemplate(null)
                      setBlock(null)
                      setColumn(null)
                      setCustomReportColumn(null)
                      setMicroServicesReportColumn(null)
                      setParticipantReportColumn(null)

                      // Clear downstream report filters
                      clearReportFilters()
                    }}
                    showClear
                    filter
                    placeholder="Select type of report"
                  />
                </div>
              ) : null}

              {type !== null && type !== undefined ? (
                <div>
                  <strong>Select report:</strong>
                  <Dropdown
                    className={styles.selectInput}
                    id="subtype"
                    value={subtype}
                    options={report_sub_type}
                    onChange={(e) => {
                      // Clear downstream report selections
                      setSubtype(e.target.value)
                      setTemplate(null)
                      setBlock(null)
                      setColumn(null)
                      setCustomReportColumn(null)
                      setMicroServicesReportColumn(null)
                      setParticipantReportColumn(null)
                      setDtreorder(false)
                      setDtfilter(false)

                      // Clear downstream report filters
                      clearReportFilters()
                    }}
                    showClear
                    filter
                    placeholder="Select report"
                    optionLabel="label"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                  />
                </div>
              ) : null}

              {subtype !== null &&
              subtype !== undefined &&
              type === ORGANIZATION_REPORTING_TYPE ? (
                <div>
                  {renderLoadingLabel("Select template:", template_loading)}
                  <Dropdown
                    className={styles.selectInput}
                    id="template"
                    value={template}
                    options={template_options}
                    onChange={(e) => {
                      setTemplate(e.target.value)
                      setBlock(null)
                      setColumn(null)
                    }}
                    showClear
                    filter
                    placeholder="Select template"
                    optionLabel="label"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    scrollHeight="min(60vh, 500px)"
                    loading={template_loading}
                  />
                </div>
              ) : null}

              {template !== null &&
              template !== undefined &&
              subtype !== undefined ? (
                <div>
                  {renderLoadingLabel("Select blocks:", block_loading)}
                  <MultiSelect
                    className={styles.selectInput}
                    id="block"
                    value={block}
                    options={block_options}
                    onChange={(e) => {
                      setBlock(e.target.value)
                      setColumn(null)
                    }}
                    maxSelectedLabels={1}
                    showClear
                    filter
                    placeholder="Select blocks"
                    optionLabel="label"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    scrollHeight="min(60vh, 500px)"
                    loading={block_loading}
                  />
                </div>
              ) : null}

              {subtype !== null &&
              subtype !== undefined &&
              type === "Custom Reporting" ? (
                <div>
                  {renderLoadingLabel(
                    "Select reporting fields:",
                    custom_column_loading,
                  )}
                  <MultiSelect
                    className={styles.selectInput}
                    id="custom_column"
                    value={custom_report_column}
                    options={custom_column_options}
                    onChange={(e) => {
                      setCustomReportColumn(e.target.value)
                      setDtreorder(false)
                      setDtfilter(false)
                    }}
                    maxSelectedLabels={1}
                    showClear
                    filter
                    placeholder="Select reporting fields"
                    optionLabel="label"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    scrollHeight="min(60vh, 500px)"
                    loading={custom_column_loading}
                  />
                </div>
              ) : null}

              {subtype !== null &&
              subtype !== undefined &&
              type === "Microservices Reporting" ? (
                <div>
                  {renderLoadingLabel(
                    "Select reporting fields:",
                    microservices_column_loading,
                  )}
                  <MultiSelect
                    className={styles.selectInput}
                    id="microservices_column"
                    value={microservices_report_column}
                    options={microservices_column_options}
                    onChange={(e) => {
                      setMicroServicesReportColumn(e.target.value)
                      setDtreorder(false)
                      setDtfilter(false)
                    }}
                    maxSelectedLabels={1}
                    showClear
                    filter
                    placeholder="Select reporting fields"
                    optionLabel="label"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    scrollHeight="min(60vh, 500px)"
                    loading={microservices_column_loading}
                  />
                </div>
              ) : null}
              {subtype !== null &&
              subtype !== undefined &&
              type === PARTICIPANT_REPORT_TYPE ? (
                <div>
                  {renderLoadingLabel(
                    "Select reporting fields:",
                    participant_column_loading,
                  )}
                  <MultiSelect
                    className={styles.selectInput}
                    id="participant_column"
                    value={participant_report_column}
                    options={participant_column_options}
                    onChange={(e) => {
                      setParticipantReportColumn(e.target.value)
                      setDtreorder(false)
                      setDtfilter(false)
                    }}
                    maxSelectedLabels={1}
                    showClear
                    filter
                    placeholder="Select reporting fields"
                    optionLabel="label"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    scrollHeight="min(60vh, 500px)"
                    loading={participant_column_loading}
                  />
                </div>
              ) : null}

              {block != null && block != undefined && template != undefined ? (
                <div>
                  {renderLoadingLabel(
                    "Select reporting fields:",
                    column_loading,
                  )}
                  <MultiSelect
                    className={styles.selectInput}
                    id="column"
                    value={column}
                    options={column_options}
                    onChange={(e) => setColumn(e.target.value)}
                    maxSelectedLabels={1}
                    showClear
                    filter
                    placeholder="Select reporting fields"
                    optionLabel="label"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    scrollHeight="min(60vh, 500px)"
                    loading={column_loading}
                  />
                </div>
              ) : null}
            </div>
          </Panel>

          {/* Center panel */}
          <Panel
            headerTemplate={(options) =>
              sectionPanelTemplate(options, "Report Filters")
            }
            className={styles.mypanel}
          >
            <div className={styles.filter}>
              {!shouldHideFilter("location") && (
                <div>
                  {renderLoadingLabel("Locations:", location_loading)}
                  <div className={styles.helpText}>
                    {locationFilterHelpText}
                  </div>
                  <MultiSelect
                    className={styles.selectInput}
                    id="location"
                    value={location}
                    options={location_options}
                    onChange={(e) => {
                      setLocation(e.target.value)
                    }}
                    maxSelectedLabels={1}
                    showClear
                    filter
                    placeholder={"Select locations"}
                    optionLabel="label"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    scrollHeight="min(60vh, 500px)"
                    loading={location_loading}
                  />
                </div>
              )}

              {!shouldHideFilter("program") && (
                <div>
                  {renderLoadingLabel("Programs:", program_loading)}
                  <div className={styles.helpText}>{programFilterHelpText}</div>
                  <MultiSelect
                    className={styles.selectInput}
                    id="program"
                    value={program}
                    options={program_options}
                    onChange={(e) => {
                      setProgram(e.target.value)
                    }}
                    maxSelectedLabels={1}
                    showClear
                    filter
                    placeholder={"Select programs"}
                    optionLabel="label"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    scrollHeight="min(60vh, 500px)"
                    loading={program_loading}
                  />
                </div>
              )}

              {!shouldHideFilter("participant_status") && (
                <div>
                  {renderLoadingLabel("Participant Status:", status_loading)}
                  <div className={styles.helpText}>
                    {participantStatusFilterHelpText}
                  </div>
                  <MultiSelect
                    className={styles.selectInput}
                    id="status"
                    value={status}
                    options={status_options}
                    onChange={(e) => {
                      setStatus(e.target.value)
                    }}
                    maxSelectedLabels={1}
                    showClear
                    placeholder="Select participant status"
                    optionLabel="label"
                    scrollHeight="min(60vh, 500px)"
                    loading={status_loading}
                  />
                </div>
              )}

              {!shouldHideFilter("assigned_staff") && (
                <div>
                  {renderLoadingLabel("Assigned Staff:", staff_loading)}
                  <div className={styles.helpText}>
                    {assignedStaffFilterHelpText}
                  </div>
                  <MultiSelect
                    className={styles.selectInput}
                    id="staff"
                    value={staff}
                    options={staff_options}
                    onChange={(e) => {
                      setStaff(e.target.value)
                    }}
                    maxSelectedLabels={1}
                    showClear
                    filter
                    placeholder={"Select assigned staff"}
                    optionLabel="label"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    scrollHeight="min(60vh, 500px)"
                    loading={staff_loading}
                  />
                </div>
              )}

              {!shouldHideFilter("facilitating_staff") && (
                <div>
                  {renderLoadingLabel("Facilitating Staff:", staff_loading)}
                  <div className={styles.helpText}>
                    {facilitatingStaffFilterHelpText}
                  </div>
                  <MultiSelect
                    className={styles.selectInput}
                    id="facilitating_staff"
                    value={facilitating_staff}
                    options={staff_options}
                    onChange={(e) => {
                      setFacilitatingStaff(e.target.value)
                    }}
                    maxSelectedLabels={1}
                    showClear
                    filter
                    placeholder="Select facilitating staff"
                    optionValue="id"
                    optionLabel="label"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    scrollHeight="min(60vh, 500px)"
                    loading={staff_loading}
                  />
                </div>
              )}
            </div>
          </Panel>

          {/* Right panel */}
          <Panel
            className={styles.mypanel}
            headerTemplate={(options) =>
              sectionPanelTemplate(options, "Date Selections")
            }
          >
            {!isParticipantReport ? (
              <div className={styles.filter}>
                <div>
                  <strong>Select reporting date range:</strong>
                </div>
                <div>
                  <DateRangePicker
                    onChange={(item) => setDateRange([item.selection])}
                    showSelectionPreview={true}
                    moveRangeOnFirstSelection={false}
                    minDate={new Date("2019-12-01")}
                    maxDate={new Date()}
                    ranges={date_range}
                    direction="horizontal"
                    editableDateInputs={true}
                    hasCustomRendering={true}
                    dayContentRenderer={customDayContent}
                    dateDisplayFormat={"MM-dd-yyyy"}
                    renderStaticRangeLabel={renderStaticRangeLabel}
                    staticRanges={[
                      ...defaultStaticRanges,
                      {
                        label: "This Year",
                        range: () => ({
                          startDate: startOfYear(new Date()),
                          endDate: endOfDay(new Date()),
                        }),
                        isSelected(range) {
                          const definedRange = this.range()
                          return (
                            isSameDay(
                              range.startDate,
                              definedRange.startDate,
                            ) && isSameDay(range.endDate, definedRange.endDate)
                          )
                        },
                      },
                      {
                        label: "Last Year",
                        range: () => ({
                          startDate: startOfYear(addYears(new Date(), -1)),
                          endDate: endOfYear(addYears(new Date(), -1)),
                        }),
                        isSelected(range) {
                          const definedRange = this.range()
                          return (
                            isSameDay(
                              range.startDate,
                              definedRange.startDate,
                            ) && isSameDay(range.endDate, definedRange.endDate)
                          )
                        },
                      },
                      {
                        label: "Today",
                        hasCustomRendering: true,
                        range: () => ({
                          startDate: new Date(),
                          endDate: new Date(),
                        }),
                        isSelected() {
                          return true
                        },
                      },
                    ]}
                    inputRanges={[
                      {
                        ...defaultInputRanges[0],
                        label: "Days up to Today",
                      },
                    ]}
                  />
                </div>
              </div>
            ) : (
              <div className={styles.filter}>
                <Message
                  style={{ paddingTop: "1.5rem", paddingBottom: "1.5rem" }}
                  severity="warn"
                  text="Date range selection is not applicable for Participant Reports."
                />
              </div>
            )}
          </Panel>
        </div>
        <div className={styles.button}>
          <Button
            type="button"
            label="Generate Report"
            icon="pi pi-eye"
            loading={loading}
            className={styles.report_button}
            disabled={
              (custom_report_column === null &&
                column === null &&
                microservices_report_column === null &&
                participant_report_column === null) |
              (organization === null) |
              (organization === undefined) |
              (clear === true)
            }
            onClick={() => {
              setLoading(true)
              setDtreorder(false)
              setDtfilter(false)
              clearAsyncExportPolling()
              setAsyncExportInfo(null)
              if (type === ORGANIZATION_REPORTING_TYPE) {
                fetchReportWithAsyncFallback(
                  fetchorganizationreports,
                  {
                    organization_id: organization_transform,
                    template_title: template_transform,
                    block_name: block_transform,
                    columns: column_transform,
                    report_type: subtype,
                    start_date: format(date_range[0].startDate, "yyyy-MM-dd"),
                    end_date: format(date_range[0].endDate, "yyyy-MM-dd"),
                    location_id: location_transform_name,
                    program_id: program_transform_name,
                    status: status_transform,
                    staff: staff_transform_by_name,
                    facilitating_staff: facilitating_staff_transform_by_id,
                  },
                  "ReportOrganizationReports",
                )
              } else if (type === "Custom Reporting") {
                if (subtype === "ISR Individual Sessions") {
                  fetchReportWithAsyncFallback(
                    fetchisr,
                    {
                      organization_name: organization_transform_name_join,
                      location_id: location_transform_name,
                      program_id: program_transform_name,
                      start_date: format(date_range[0].startDate, "yyyy-MM-dd"),
                      end_date: format(date_range[0].endDate, "yyyy-MM-dd"),
                      staff: staff_transform_by_name,
                      // Server matches CONCAT(first, last); send names not staff IDs
                      facilitating_staff: facilitating_staff_transform_by_name,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportISR",
                  )
                } else if (subtype === "ISR Group Sessions") {
                  fetchReportWithAsyncFallback(
                    fetchisrgroup,
                    {
                      organization_name: organization_transform_name_join,
                      location_id: location_transform_name,
                      program_id: program_transform_name,
                      start_date: format(date_range[0].startDate, "yyyy-MM-dd"),
                      end_date: format(date_range[0].endDate, "yyyy-MM-dd"),
                      staff: staff_transform_by_name,
                      // Server matches CONCAT(first, last); send names not staff IDs
                      facilitating_staff: facilitating_staff_transform_by_name,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportISRGroupSession",
                  )
                } else if (subtype === "Opioid Stimulant Specific") {
                  fetchReportWithAsyncFallback(
                    fetchos,
                    {
                      organization_name: organization_transform_name_join,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportIndianaOSReport",
                  )
                } else if (subtype === "Activity Session") {
                  fetchReportWithAsyncFallback(
                    fetchac,
                    {
                      organization_name: organization_transform_name_join,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportIndianaActivitySessionReport",
                  )
                } else if (subtype === "Demographics") {
                  fetchReportWithAsyncFallback(
                    fetchdm,
                    {
                      organization_name: organization_transform_name_join,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportIndianaDemographicsReport",
                  )
                } else if (subtype === "Direct Service Hours") {
                  fetchReportWithAsyncFallback(
                    fetchds,
                    {
                      organization_name: organization_transform_name_join,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportIndianaDirectServiceReport",
                  )
                } else if (subtype === "Engagements") {
                  fetchReportWithAsyncFallback(
                    fetcheg,
                    {
                      organization_name: organization_transform_name_join,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportIndianaEngagementReport",
                  )
                } else if (subtype === "GPRA") {
                  fetchReportWithAsyncFallback(
                    fetchgp,
                    {
                      organization_name: organization_transform_name_join,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportIndianaGPRAReport",
                  )
                } else if (
                  subtype === "Individual List of Incoming Referrals"
                ) {
                  fetchReportWithAsyncFallback(
                    fetchlr,
                    {
                      organization_name: organization_transform_name_join,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportIndianaListofReferralsReport",
                  )
                } else if (subtype === "Lyft Drop-off Location") {
                  fetchReportWithAsyncFallback(
                    fetchld,
                    {
                      organization_name: organization_transform_name_join,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportIndianaLyftDropoffReport",
                  )
                } else if (subtype === "Lyft") {
                  fetchReportWithAsyncFallback(
                    fetchly,
                    {
                      organization_name: organization_transform_name_join,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportIndianaLyftReport",
                  )
                } else if (subtype === "Supervision") {
                  fetchReportWithAsyncFallback(
                    fetchsp,
                    {
                      organization_name: organization_transform_name_join,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportIndianaSupervisionReport",
                  )
                } else if (subtype === "DOC Incoming Referrals") {
                  fetchReportWithAsyncFallback(
                    fetchre,
                    {
                      organization_name: organization_transform_name_join,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportIndianaReferralsReport",
                  )
                } else if (
                  subtype === "PRIME Participant Characteristics Report"
                ) {
                  fetchReportWithAsyncFallback(
                    fetchprimechracteristics,
                    {
                      organization_id: organization_transform,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportPrimeCharacteristicsReport",
                  )
                } else if (
                  subtype === "PRIME Participant Demographics Report"
                ) {
                  fetchReportWithAsyncFallback(
                    fetchprimedemographics,
                    {
                      organization_id: organization_transform,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportPrimeDemographicsReport",
                  )
                } else if (subtype === "PRIME Intakes Report") {
                  fetchReportWithAsyncFallback(
                    fetchprimeintakes,
                    {
                      organization_name: organization_transform_name_join,
                      location_id: location_transform_name,
                      program_id: program_transform_name,
                      start_date: format(date_range[0].startDate, "yyyy-MM-dd"),
                      end_date: format(date_range[0].endDate, "yyyy-MM-dd"),
                      staff: staff_transform_by_name,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportPrimeIntakesReport",
                  )
                } else if (subtype === "PRIME Check-ins Report") {
                  fetchReportWithAsyncFallback(
                    fetchprimecheckins,
                    {
                      organization_name: organization_transform_name_join,
                      location_id: location_transform_name,
                      program_id: program_transform_name,
                      start_date: format(date_range[0].startDate, "yyyy-MM-dd"),
                      end_date: format(date_range[0].endDate, "yyyy-MM-dd"),
                      staff: staff_transform_by_name,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportPrimeCheckInsReport",
                  )
                } else if (subtype === "PRIME GPRA Report") {
                  fetchReportWithAsyncFallback(
                    fetchprimegpra,
                    {
                      organization_name: organization_transform_name_join,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportPrimeGPRAReport",
                  )
                } else if (subtype === "Contact Log") {
                  fetchReportWithAsyncFallback(
                    fetchcl,
                    {
                      organization_name: organization_transform_name_join,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportIndianaContactLogReport",
                  )
                } else if (
                  subtype === "New Hampshire Medicaid Billing Report"
                ) {
                  fetchReportWithAsyncFallback(
                    fetchnhmedicaidbilling,
                    {
                      organization_name: organization_transform_name_join,
                      start_date: format(date_range[0].startDate, "yyyy-MM-dd"),
                      end_date: format(date_range[0].endDate, "yyyy-MM-dd"),
                      columns: selectedColumnsForRequest,
                    },
                    "ReportNhMedicaidBillingReport",
                  )
                }
              } else if (type === "Microservices Reporting") {
                if (subtype === GPRA_REPORT_SUBTYPE) {
                  fetchReportWithAsyncFallback(
                    fetchgpra,
                    {
                      organization_name: organization_transform_name_join,
                      program_id: program_transform_name_gpra,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportGPRAReports",
                  )
                } else if (subtype === KIOSK_CHECK_IN_ACTIVITY_REPORT_SUBTYPE) {
                  fetchReportWithAsyncFallback(
                    fetchkioskactivity,
                    {
                      organization_name: organization_transform_name_join,
                      start_date: format(date_range[0].startDate, "yyyy-MM-dd"),
                      end_date: format(date_range[0].endDate, "yyyy-MM-dd"),
                      locations: location_transform_name,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportKioskActivityReport",
                  )
                } else if (subtype === REFERRALS_REPORT_SUBTYPE) {
                  fetchReportWithAsyncFallback(
                    fetchreferrals,
                    {
                      organization_name: organization_transform_name_join,
                      location_id: location_transform_name,
                      program_id: program_transform_name,
                      start_date: format(date_range[0].startDate, "yyyy-MM-dd"),
                      end_date: format(date_range[0].endDate, "yyyy-MM-dd"),
                      staff: staff_transform_by_name,
                      facilitating_staff: facilitating_staff_transform_by_id,
                      status: status_transform,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportReferralsReport",
                  )
                } else if (
                  subtype === ASSESSMENTS_BARC_10_MONTHLY_REPORT_SUBTYPE
                ) {
                  fetchReportWithAsyncFallback(
                    fetchbarc10monthly,
                    {
                      organization_name: organization_transform_name_join,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportBarc10MonthlyReport",
                  )
                } else if (subtype === ASSESSMENTS_BARC_10_REPORT_SUBTYPE) {
                  fetchReportWithAsyncFallback(
                    fetchbarc10individual,
                    {
                      organization_name: organization_transform_name_join,
                      location_id: location_transform_name,
                      program_id: program_transform_name,
                      start_date: format(date_range[0].startDate, "yyyy-MM-dd"),
                      end_date: format(date_range[0].endDate, "yyyy-MM-dd"),
                      staff: staff_transform_by_name,
                      facilitating_staff: facilitating_staff_transform_by_name,
                      status: status_transform,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportBarc10IndividualReport",
                  )
                } else if (subtype === TELERECOVERY_SESSIONS_REPORT_SUBTYPE) {
                  fetchReportWithAsyncFallback(
                    fetchtelerecoverysessions,
                    {
                      organization_name: organization_transform_name_join,
                      location_id: location_transform_name,
                      program_id: program_transform_name,
                      start_date: format(date_range[0].startDate, "yyyy-MM-dd"),
                      end_date: format(date_range[0].endDate, "yyyy-MM-dd"),
                      staff: staff_transform_by_id,
                      facilitating_staff: facilitating_staff_transform_by_id,
                      status: status_transform,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportTelerecoverySessionsReport",
                  )
                }
              } else if (type === PARTICIPANT_REPORT_TYPE) {
                if (subtype === "Participant Contact Information") {
                  fetchReportWithAsyncFallback(
                    fetchparticipantcontact,
                    {
                      organization_name: organization_transform_name_join,
                      location_names: location,
                      program_names: program,
                      participant_statuses: status,
                      assigned_staff_names: staff,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportParticipantContactReport",
                  )
                } else if (subtype === "Participant Demographics") {
                  fetchReportWithAsyncFallback(
                    fetchparticipantdemographics,
                    {
                      organization_name: organization_transform_name_join,
                      location_names: location,
                      program_names: program,
                      participant_statuses: status,
                      assigned_staff_names: staff,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportParticipantDemographicsReport",
                  )
                } else if (subtype === "Participant Engaged Services") {
                  fetchReportWithAsyncFallback(
                    fetchparticipantengaged,
                    {
                      organization_name: organization_transform_name_join,
                      location_names: location,
                      program_names: program,
                      participant_statuses: status,
                      assigned_staff_names: staff,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportParticipantEngagedReport",
                  )
                } else if (subtype === "Participant Recovery Characteristics") {
                  fetchReportWithAsyncFallback(
                    fetchparticipantcharacteristics,
                    {
                      organization_name: organization_transform_name_join,
                      location_names: location,
                      program_names: program,
                      participant_statuses: status,
                      assigned_staff_names: staff,
                      columns: selectedColumnsForRequest,
                    },
                    "ReportParticipantCharacteristicsReport",
                  )
                }
              }
            }}
          />
          <Button
            type="button"
            label="Clear Data"
            icon="pi pi-eye-slash"
            className={styles.report_button}
            disabled={clear === false}
            onClick={() => {
              clearAsyncExportPolling()
              setAsyncExportInfo(null)
              setClear(false)
              setDtreorder(false)
              setDtfilter(false)
            }}
          />
        </div>
        {asyncExportInfo ? (
          <div className={styles.asyncExportStatus}>
            <Message
              severity={getAsyncExportSeverity(asyncExportInfo.status)}
              className={`${styles.asyncExportMessage} ${
                isAsyncExportInProgress(asyncExportInfo.status)
                  ? styles.asyncExportMessageProcessing
                  : asyncExportInfo.status === "READY" &&
                      asyncExportInfo.downloadUrl
                    ? styles.asyncExportMessageReady
                    : isAsyncExportErrorStatus(asyncExportInfo.status)
                      ? styles.asyncExportMessageError
                      : ""
              }`}
              text={
                <div className={styles.asyncExportMessageBody}>
                  {isAsyncExportInProgress(asyncExportInfo.status) ? (
                    (() => {
                      const exportingRowsMessage = formatExportingRowsMessage(
                        asyncExportInfo.estimatedRows,
                      )
                      return (
                        <div className={styles.asyncExportProcessingRow}>
                          <i
                            className={`pi pi-spin pi-cog ${styles.asyncExportCogIcon}`}
                            aria-hidden="true"
                          />
                          <div className={styles.asyncExportProcessingText}>
                            <div className={styles.asyncExportProcessingLabel}>
                              Processing…{" "}
                              {formatElapsedSeconds(
                                asyncExportInfo.elapsedSeconds,
                              )}
                            </div>
                            {exportingRowsMessage ? (
                              <div className={styles.asyncExportExportingNote}>
                                {exportingRowsMessage}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      )
                    })()
                  ) : asyncExportInfo.status === "READY" &&
                    asyncExportInfo.downloadUrl ? (
                    (() => {
                      const statsLine = formatExportReadyStatsLine(
                        asyncExportInfo.rowCount,
                        asyncExportInfo.fileSizeBytes,
                      )
                      const filename = getAsyncExportDownloadFilename(
                        asyncExportInfo.downloadFilename,
                        asyncExportInfo.jobId,
                      )
                      return (
                        <div className={styles.asyncExportReadyRow}>
                          <i
                            className={`pi pi-check-circle ${styles.asyncExportReadyIcon}`}
                            aria-hidden="true"
                          />
                          <div className={styles.asyncExportReadyText}>
                            <div className={styles.asyncExportReadyTitle}>
                              Report export is ready for download
                            </div>
                            {statsLine ? <div>{statsLine}</div> : null}
                            <div className={styles.asyncExportRetentionNote}>
                              This file will be deleted after 7 days.
                            </div>
                            <Button
                              label="Download Report"
                              icon="pi pi-download"
                              className={styles.asyncExportDownloadButton}
                              onClick={() =>
                                downloadAsyncExportFile(
                                  asyncExportInfo.downloadUrl,
                                  asyncExportInfo.jobId,
                                  asyncExportInfo.downloadFilename,
                                )
                              }
                            />
                            {filename ? (
                              <div className={styles.asyncExportFilename}>
                                {filename}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      )
                    })()
                  ) : isAsyncExportErrorStatus(asyncExportInfo.status) ? (
                    <div className={styles.asyncExportErrorRow}>
                      <i
                        className={`pi pi-ban ${styles.asyncExportErrorIcon}`}
                        aria-hidden="true"
                      />
                      <div className={styles.asyncExportErrorText}>
                        <div className={styles.asyncExportErrorTitle}>
                          An error has occurred
                        </div>
                        <div className={styles.asyncExportErrorBody}>
                          Please capture a screenshot of the report parameters
                          and contact{" "}
                          <a
                            href={`mailto:${ASYNC_EXPORT_SUPPORT_EMAIL}`}
                            className={styles.asyncExportSupportLink}
                          >
                            {ASYNC_EXPORT_SUPPORT_EMAIL}
                          </a>{" "}
                          with details.
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <strong>Async export status:</strong>{" "}
                      {asyncExportInfo.message || "Preparing export file..."}
                    </div>
                  )}
                  {asyncExportInfo.softTimeoutReached &&
                  isAsyncExportInProgress(asyncExportInfo.status) ? (
                    <div className={styles.asyncExportSoftTimeoutNote}>
                      This export is taking longer than usual. We are still
                      checking for your download. You can leave this page; the
                      job will continue on the server.
                    </div>
                  ) : null}
                </div>
              }
            />
          </div>
        ) : null}
        {!asyncExportInfo ? (
          <div ref={reportsTableSectionRef} className={styles.table}>
            {organization_reports_data_transform && column && clear && (
              <div>
                <Tooltip target=".export-buttons>button" position="bottom" />
                <DataTable
                  ref={dt}
                  paginator
                  rows={5}
                  selectionMode="multiple"
                  sortMode="multiple"
                  emptyMessage="No data available"
                  exportFilename={template}
                  value={organization_reports_data_transform}
                  header={headers}
                  dataKey="id"
                  responsiveLayout="scroll"
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                  showGridlines
                  resizableColumns
                  removableSort
                  columnResizeMode="fit"
                  reorderableColumns
                  onColReorder={(e) => setDtreorder(e)}
                  onValueChange={(e) => setDtfilter(e)}
                >
                  <Column
                    key="organization_name"
                    field="organization_name"
                    sortable
                    header="organization_name"
                    style={{ minWidth: "15rem" }}
                    filter
                    filterPlaceholder="Search by name"
                  />
                  {subtype !== "Activity" ? (
                    <Column
                      key="assigned_staff_id"
                      field="assigned_staff_id"
                      sortable
                      header="assigned_staff_id"
                      style={{ minWidth: "15rem" }}
                      filter
                      filterPlaceholder="Search by name"
                    />
                  ) : null}
                  <Column
                    key="facilitating_staff_alias_id"
                    field="facilitating_staff_alias_id"
                    sortable
                    header="facilitating_staff_alias_id"
                    style={{ minWidth: "15rem" }}
                    filter
                    filterPlaceholder="Search by name"
                  />
                  <Column
                    key="facilitating_staff_name_first"
                    field="facilitating_staff_name_first"
                    sortable
                    header="facilitating_staff_name_first"
                    style={{ minWidth: "15rem" }}
                    filter
                    filterPlaceholder="Search by name"
                  />
                  <Column
                    key="facilitating_staff_name_last"
                    field="facilitating_staff_name_last"
                    sortable
                    header="facilitating_staff_name_last"
                    style={{ minWidth: "15rem" }}
                    filter
                    filterPlaceholder="Search by name"
                  />
                  {subtype !== "Activity" ? (
                    <Column
                      key="participant_membership_id"
                      field="participant_membership_id"
                      sortable
                      header="participant_membership_id"
                      style={{ minWidth: "15rem" }}
                      filter
                      filterPlaceholder="Search by name"
                    />
                  ) : null}
                  {subtype !== "Activity" ? (
                    <Column
                      key="participant_assigned_locations"
                      field="participant_assigned_locations"
                      sortable
                      header="participant_assigned_locations"
                      style={{ minWidth: "15rem" }}
                      filter
                      filterPlaceholder="Search by name"
                    />
                  ) : null}
                  {subtype !== "Activity" ? (
                    <Column
                      key="participant_assigned_programs"
                      field="participant_assigned_programs"
                      sortable
                      header="participant_assigned_programs"
                      style={{ minWidth: "15rem" }}
                      filter
                      filterPlaceholder="Search by name"
                    />
                  ) : null}
                  {subtype !== "Activity" ? (
                    <Column
                      key="participant_alias"
                      field="participant_alias"
                      sortable
                      header="participant_alias"
                      style={{ minWidth: "15rem" }}
                      filter
                      filterPlaceholder="Search by name"
                    />
                  ) : null}
                  {subtype !== "Activity" ? (
                    <Column
                      key="participant_name_first"
                      field="participant_name_first"
                      sortable
                      header="participant_name_first"
                      style={{ minWidth: "15rem" }}
                      filter
                      filterPlaceholder="Search by name"
                    />
                  ) : null}
                  {subtype !== "Activity" ? (
                    <Column
                      key="participant_name_last"
                      field="participant_name_last"
                      sortable
                      header="participant_name_last"
                      style={{ minWidth: "15rem" }}
                      filter
                      filterPlaceholder="Search by name"
                    />
                  ) : null}
                  {subtype !== "Activity" ? (
                    <Column
                      key="participant_email_account"
                      field="participant_email_account"
                      sortable
                      header="participant_email_account"
                      style={{ minWidth: "15rem" }}
                      filter
                      filterPlaceholder="Search by name"
                    />
                  ) : null}
                  {subtype !== "Activity" ? (
                    <Column
                      key="participant_membership_status"
                      field="participant_membership_status"
                      sortable
                      header="participant_membership_status"
                      style={{ minWidth: "15rem" }}
                      filter
                      filterPlaceholder="Search by name"
                    />
                  ) : null}
                  {subtype === "Session" ? (
                    <Column
                      key="session_id"
                      field="session_id"
                      sortable
                      header="session_id"
                      style={{ minWidth: "15rem" }}
                      filter
                      filterPlaceholder="Search by id"
                    />
                  ) : null}
                  {column.map((col) => {
                    const columnsToRender = [col]

                    if (col === "supervision__staff_membership_id") {
                      columnsToRender.push("supervision__staff_name")
                    }

                    return columnsToRender.map((field) => (
                      <Column
                        key={field}
                        field={field}
                        sortable
                        header={field}
                        style={{ minWidth: "15rem" }}
                        filter
                        filterPlaceholder="Search by name"
                      />
                    ))
                  })}
                </DataTable>
              </div>
            )}
            {subtype === "ISR Individual Sessions" &&
              isrdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={isrdata.ReportISR.results}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "ISR Group Sessions" &&
              isrgroupdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={isrgroupdata.ReportISRGroupSession.results}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === GPRA_REPORT_SUBTYPE &&
              gpradata &&
              microservices_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={gpradata.ReportGPRAReports.results}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {microservices_column_data.ReportMicroservicesColumns.map(
                      (col) => {
                        if (
                          microservices_report_column !== null &&
                          microservices_report_column.includes(col.column_name)
                        ) {
                          return (
                            <Column
                              key={col.column_name}
                              field={col.column_name}
                              sortable
                              header={col.column_name}
                              style={{ minWidth: "15rem" }}
                              filter
                              filterPlaceholder="Search by name"
                            />
                          )
                        }
                      },
                    )}
                  </DataTable>
                </div>
              )}
            {subtype === REFERRALS_REPORT_SUBTYPE &&
              referralsdata &&
              microservices_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={referralsdata.ReportReferralsReport.results}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {microservices_column_data.ReportMicroservicesColumns.map(
                      (col) => {
                        if (
                          microservices_report_column !== null &&
                          microservices_report_column.includes(col.column_name)
                        ) {
                          return (
                            <Column
                              key={col.column_name}
                              field={col.column_name}
                              sortable
                              header={col.column_name}
                              style={{ minWidth: "15rem" }}
                              filter
                              filterPlaceholder="Search by name"
                            />
                          )
                        }
                      },
                    )}
                  </DataTable>
                </div>
              )}
            {subtype === KIOSK_CHECK_IN_ACTIVITY_REPORT_SUBTYPE &&
              kioskactivitydata &&
              microservices_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={kioskactivitydata.ReportKioskActivityReport.results}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {microservices_column_data.ReportMicroservicesColumns.map(
                      (col) => {
                        if (
                          microservices_report_column !== null &&
                          microservices_report_column.includes(col.column_name)
                        ) {
                          return (
                            <Column
                              key={col.column_name}
                              field={col.column_name}
                              sortable
                              header={col.column_name}
                              style={{ minWidth: "15rem" }}
                              filter
                              filterPlaceholder="Search by name"
                            />
                          )
                        }
                      },
                    )}
                  </DataTable>
                </div>
              )}
            {subtype === "Participant Contact Information" &&
              participantcontactdata &&
              participant_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={
                      participantcontactdata.ReportParticipantContactReport
                        .results
                    }
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {participant_column_data.ReportParticipantColumns.map(
                      (col) => {
                        const normalizedColumnName =
                          normalizeParticipantColumnName(col.column_name)

                        if (
                          selectedParticipantReportColumns.includes(
                            normalizedColumnName,
                          )
                        ) {
                          return (
                            <Column
                              key={normalizedColumnName}
                              field={normalizedColumnName}
                              sortable
                              header={normalizedColumnName}
                              style={{ minWidth: "15rem" }}
                              filter
                              filterPlaceholder="Search by name"
                              body={(rowData) =>
                                getParticipantFieldValue(
                                  rowData,
                                  normalizedColumnName,
                                )
                              }
                            />
                          )
                        }
                      },
                    )}
                  </DataTable>
                </div>
              )}
            {subtype === "Participant Demographics" &&
              participantdemographicsdata &&
              participant_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={
                      participantdemographicsdata
                        .ReportParticipantDemographicsReport.results
                    }
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {participant_column_data.ReportParticipantColumns.map(
                      (col) => {
                        const normalizedColumnName =
                          normalizeParticipantColumnName(col.column_name)

                        if (
                          selectedParticipantReportColumns.includes(
                            normalizedColumnName,
                          )
                        ) {
                          return (
                            <Column
                              key={normalizedColumnName}
                              field={normalizedColumnName}
                              sortable
                              header={normalizedColumnName}
                              style={{ minWidth: "15rem" }}
                              filter
                              filterPlaceholder="Search by name"
                              body={(rowData) =>
                                getParticipantFieldValue(
                                  rowData,
                                  normalizedColumnName,
                                )
                              }
                            />
                          )
                        }
                      },
                    )}
                  </DataTable>
                </div>
              )}
            {subtype === "Participant Engaged Services" &&
              participantengageddata &&
              participant_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={
                      participantengageddata.ReportParticipantEngagedReport
                        .results
                    }
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {participant_column_data.ReportParticipantColumns.map(
                      (col) => {
                        const normalizedColumnName =
                          normalizeParticipantColumnName(col.column_name)

                        if (
                          selectedParticipantReportColumns.includes(
                            normalizedColumnName,
                          )
                        ) {
                          return (
                            <Column
                              key={normalizedColumnName}
                              field={normalizedColumnName}
                              sortable
                              header={normalizedColumnName}
                              style={{ minWidth: "15rem" }}
                              filter
                              filterPlaceholder="Search by name"
                              body={(rowData) =>
                                getParticipantFieldValue(
                                  rowData,
                                  normalizedColumnName,
                                )
                              }
                            />
                          )
                        }
                      },
                    )}
                  </DataTable>
                </div>
              )}
            {subtype === "Participant Recovery Characteristics" &&
              participantcharacteristicsdata &&
              participant_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={
                      participantcharacteristicsdata
                        .ReportParticipantCharacteristicsReport.results
                    }
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {participant_column_data.ReportParticipantColumns.map(
                      (col) => {
                        const normalizedColumnName =
                          normalizeParticipantColumnName(col.column_name)

                        if (
                          selectedParticipantReportColumns.includes(
                            normalizedColumnName,
                          )
                        ) {
                          return (
                            <Column
                              key={normalizedColumnName}
                              field={normalizedColumnName}
                              sortable
                              header={normalizedColumnName}
                              style={{ minWidth: "15rem" }}
                              filter
                              filterPlaceholder="Search by name"
                              body={(rowData) =>
                                getParticipantFieldValue(
                                  rowData,
                                  normalizedColumnName,
                                )
                              }
                            />
                          )
                        }
                      },
                    )}
                  </DataTable>
                </div>
              )}
            {subtype === "Opioid Stimulant Specific" &&
              osdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={osdata.ReportIndianaOSReport.results}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "Contact Log" &&
              cldata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={cldata.ReportIndianaContactLogReport.results}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "New Hampshire Medicaid Billing Report" &&
              nhmedicaidbillingdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={
                      nhmedicaidbillingdata.ReportNhMedicaidBillingReport
                        .results
                    }
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}

            {subtype === "Activity Session" &&
              acdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={acdata.ReportIndianaActivitySessionReport.results}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "Demographics" &&
              dmdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={dmdata.ReportIndianaDemographicsReport.results}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "Direct Service Hours" &&
              dsdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={dsdata.ReportIndianaDirectServiceReport.results}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "Engagements" &&
              egdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={egdata.ReportIndianaEngagementReport.results}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "GPRA" && gpdata && custom_report_column && clear && (
              <div>
                <Tooltip target=".export-buttons>button" position="bottom" />
                <DataTable
                  ref={dt}
                  paginator
                  rows={5}
                  selectionMode="multiple"
                  sortMode="multiple"
                  emptyMessage="No data available"
                  exportFilename={subtype}
                  value={gpdata.ReportIndianaGPRAReport.results}
                  header={headers}
                  dataKey="id"
                  responsiveLayout="scroll"
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                  showGridlines
                  resizableColumns
                  removableSort
                  columnResizeMode="fit"
                  reorderableColumns
                  onColReorder={(e) => setDtreorder(e)}
                  onValueChange={(e) => setDtfilter(e)}
                >
                  {custom_column_data.ReportCustomColumn.map((col) => {
                    if (
                      custom_report_column !== null &&
                      custom_report_column.includes(col.column_name)
                    ) {
                      return (
                        <Column
                          key={col.column_name}
                          field={col.column_name}
                          sortable
                          header={col.column_name}
                          style={{ minWidth: "15rem" }}
                          filter
                          filterPlaceholder="Search by name"
                        />
                      )
                    }
                  })}
                </DataTable>
              </div>
            )}
            {subtype === "Individual List of Incoming Referrals" &&
              lrdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={lrdata.ReportIndianaListofReferralsReport.results}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "Lyft Drop-off Location" &&
              lddata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={lddata.ReportIndianaLyftDropoffReport.results}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "Lyft" && lydata && custom_report_column && clear && (
              <div>
                <Tooltip target=".export-buttons>button" position="bottom" />
                <DataTable
                  ref={dt}
                  paginator
                  rows={5}
                  selectionMode="multiple"
                  sortMode="multiple"
                  emptyMessage="No data available"
                  exportFilename={subtype}
                  value={lydata.ReportIndianaLyftReport.results}
                  header={headers}
                  dataKey="id"
                  responsiveLayout="scroll"
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                  showGridlines
                  resizableColumns
                  removableSort
                  columnResizeMode="fit"
                  reorderableColumns
                  onColReorder={(e) => setDtreorder(e)}
                  onValueChange={(e) => setDtfilter(e)}
                >
                  {custom_column_data.ReportCustomColumn.map((col) => {
                    if (
                      custom_report_column !== null &&
                      custom_report_column.includes(col.column_name)
                    ) {
                      return (
                        <Column
                          key={col.column_name}
                          field={col.column_name}
                          sortable
                          header={col.column_name}
                          style={{ minWidth: "15rem" }}
                          filter
                          filterPlaceholder="Search by name"
                        />
                      )
                    }
                  })}
                </DataTable>
              </div>
            )}
            {subtype === "Supervision" &&
              spdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={spdata.ReportIndianaSupervisionReport.results}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "DOC Incoming Referrals" &&
              redata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={redata.ReportIndianaReferralsReport.results}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "PRIME Participant Characteristics Report" &&
              primecharacteristicsdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={
                      primecharacteristicsdata.ReportPrimeCharacteristicsReport
                        .results
                    }
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "PRIME Participant Demographics Report" &&
              primedemographicsdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={
                      primedemographicsdata.ReportPrimeDemographicsReport
                        .results
                    }
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "PRIME Intakes Report" &&
              primeintakesdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={primeintakesdata.ReportPrimeIntakesReport.results}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "PRIME Check-ins Report" &&
              primecheckinsdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={primecheckinsdata.ReportPrimeCheckInsReport.results}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "PRIME GPRA Report" &&
              primegpradata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={primegpradata.ReportPrimeGPRAReport.results}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === ASSESSMENTS_BARC_10_MONTHLY_REPORT_SUBTYPE &&
              barc10monthlydata &&
              microservices_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={barc10monthlydata.ReportBarc10MonthlyReport.results}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {microservices_column_data.ReportMicroservicesColumns.map(
                      (col) => {
                        if (
                          microservices_report_column !== null &&
                          microservices_report_column.includes(col.column_name)
                        ) {
                          return (
                            <Column
                              key={col.column_name}
                              field={col.column_name}
                              sortable
                              header={col.column_name}
                              style={{ minWidth: "15rem" }}
                              filter
                              filterPlaceholder="Search by name"
                            />
                          )
                        }
                      },
                    )}
                  </DataTable>
                </div>
              )}
            {subtype === ASSESSMENTS_BARC_10_REPORT_SUBTYPE &&
              barc10individualdata &&
              microservices_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={
                      barc10individualdata.ReportBarc10IndividualReport.results
                    }
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {microservices_column_data.ReportMicroservicesColumns.map(
                      (col) => {
                        if (
                          microservices_report_column !== null &&
                          microservices_report_column.includes(col.column_name)
                        ) {
                          return (
                            <Column
                              key={col.column_name}
                              field={col.column_name}
                              sortable
                              header={col.column_name}
                              style={{ minWidth: "15rem" }}
                              filter
                              filterPlaceholder="Search by name"
                            />
                          )
                        }
                      },
                    )}
                  </DataTable>
                </div>
              )}
            {subtype === TELERECOVERY_SESSIONS_REPORT_SUBTYPE &&
              telerecoverysessionsdata &&
              microservices_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={
                      telerecoverysessionsdata.ReportTelerecoverySessionsReport
                        .results
                    }
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {microservices_column_data.ReportMicroservicesColumns.map(
                      (col) => {
                        if (
                          microservices_report_column !== null &&
                          microservices_report_column.includes(col.column_name)
                        ) {
                          return (
                            <Column
                              key={col.column_name}
                              field={col.column_name}
                              sortable
                              header={col.column_name}
                              style={{ minWidth: "15rem" }}
                              filter
                              filterPlaceholder="Search by name"
                            />
                          )
                        }
                      },
                    )}
                  </DataTable>
                </div>
              )}
          </div>
        ) : null}
      </div>
    </>
  )
}
